/**
 * Request serializer registry — maps a serializer *name* to the FlatBuffer
 * serialization function for outbound API request bodies.
 *
 * The binding between an endpoint and its serializer is declared **at the
 * endpoint**, not inferred from its URL: an RTK Query endpoint opts in by
 * naming its serializer in `extraOptions.fbSerializer`, and
 * `flatBufferBaseQuery` looks that name up here. An endpoint that names no
 * serializer sends plain JSON.
 *
 * Names come from the `SerializerName` union below, so a typo is a compile
 * error rather than a silent JSON fallthrough, and two endpoints can never
 * resolve to each other's packer no matter what their paths look like.
 *
 * Serializers are registered lazily as endpoints are migrated.
 */

import * as flatbuffers from "flatbuffers";
import { wrapEnvelope } from "../envelope";
import { formatUuidT } from "../display";

// Archive entity T types for request packing
import { MessageT } from "../generated/ln2/message";
import { SimulationScenarioT } from "../generated/api/simulation-scenario";
import { OrganizationT } from "../generated/archive/organization";
import { ServerT } from "../generated/archive/server";
import { SiteNetworkT } from "../generated/net-config/site-network";
import { NicConfigT } from "../generated/net-config/nic-config";
import { BondConfigT } from "../generated/net-config/bond-config";
import { VlanConfigT } from "../generated/net-config/vlan-config";
import { RouteConfigT } from "../generated/net-config/route-config";
import { RuleConfigT } from "../generated/net-config/rule-config";
import { AcceptRa } from "../generated/net-config/accept-ra";
import { AdapterT } from "../generated/archive/adapter";
import { ChannelT } from "../generated/archive/channel";
import { ChannelActiveRequestT } from "../generated/archive/channel-active-request";
import { BankT } from "../generated/archive/bank";
import { BankFloorT } from "../generated/archive/bank-floor";
import { LiftT } from "../generated/archive/lift";
import { LiftFloorT } from "../generated/archive/lift-floor";
import { UserT } from "../generated/archive/user";
import { ContactInfoT } from "../generated/archive/contact-info";
import { RoleT } from "../generated/archive/role";
import { RoleBankPermissionT } from "../generated/archive/role-bank-permission";
import { RoleLiftPermissionT } from "../generated/archive/role-lift-permission";
import { RoleFloorPermissionT } from "../generated/archive/role-floor-permission";
import { RolePermissionGrantT } from "../generated/archive/role-permission-grant";
import { PermissionAction } from "../generated/archive/permission-action";
import { KeyT } from "../generated/archive/key";
import { CustomKeyT } from "../generated/archive/custom-key";
import { NotificationT } from "../generated/archive/notification";
import { NotificationRecipientT } from "../generated/archive/notification-recipient";
import { DebounceOverrideT } from "../generated/archive/debounce-override";
import { TimedActionT } from "../generated/archive/timed-action";
import { RecurrenceMode } from "../generated/archive/recurrence-mode";
import { ActionBankT } from "../generated/archive/action-bank";
import { ActionButtonType } from "../generated/archive/action-button-type";
import { ActionFiringMode } from "../generated/archive/action-firing-mode";
import { toActionFiringMode as toValidatedActionFiringMode } from "../../utils/actionFiringMode";
import { grantsObject, permissionGrantsToVector } from "../../pages/setupWizard/utils/roleMenu";
import { ChannelModifierT } from "../generated/archive/channel-modifier";
import { ModifierConditionT } from "../generated/archive/modifier-condition";
import { ModifierActionT } from "../generated/archive/modifier-action";
import { ModifierSequenceStepT } from "../generated/archive/modifier-sequence-step";
import { ModifierStateConditionT } from "../generated/archive/modifier-state-condition";
import { ModifierType } from "../generated/archive/modifier-type";
import { ModifierDirection } from "../generated/archive/modifier-direction";
import { ConditionOp } from "../generated/archive/condition-op";
import { ValueTransformMode } from "../generated/archive/value-transform-mode";
import { ConflictPolicy } from "../generated/archive/conflict-policy";
import { ActiveState } from "../generated/archive/active-state";
import { ModifierTimeoutPolicy } from "../generated/archive/modifier-timeout-policy";
import { ModifierDropAction } from "../generated/archive/modifier-drop-action";
import { ChannelEndpointT } from "../generated/archive/channel-endpoint";
import { ChannelMode } from "../generated/archive/channel-mode";
import { MergePolicy } from "../generated/archive/merge-policy";
import { SessionConfigT } from "../generated/session/session-config";
import { SessionCredentialsT } from "../generated/session/session-credentials";
import { SessionProtocol } from "../generated/session/session-protocol";
import { SshOptionsT } from "../generated/session/ssh-options";
import { TelnetOptionsT } from "../generated/session/telnet-options";
import { MessageContent } from "../generated/ln2/message-content";

// Api entity T types for request packing
import { ConfigurationT } from "../generated/api/configuration";
import { GroupT } from "../generated/api/group";
import { ProtocolT } from "../generated/api/protocol";
import { VendorT } from "../generated/api/vendor";
import { MapT } from "../generated/api/map";
import { WaypointT } from "../generated/api/waypoint";
import { GroupWaypointT } from "../generated/api/group-waypoint";
import { BankWaypointT } from "../generated/api/bank-waypoint";

// UUID types for conversion
import { UuidT } from "../generated/front-end/uuid";
import { U128T } from "../generated/u128/u128";

// CDO types for auth credential packing
import { PasswordContentT } from "../generated/cdo/password-content";

// Enums used in request bodies
import { OrgType } from "../generated/archive/org-type";
import { OrgStatus } from "../generated/archive/org-status";
import { LiftType } from "../generated/archive/lift-type";
import { ChannelType } from "../generated/archive/channel-type";
import { ControlWriteMode } from "../generated/archive/control-write-mode";
import { AdapterType } from "../generated/archive/adapter-type";
import { AdapterProtocol } from "../generated/archive/adapter-protocol";
import { AdapterAuth } from "../generated/archive/adapter-auth";
import { VendorFloorIndexing } from "../generated/archive/vendor-floor-indexing";
import { OverlayLifts } from "../generated/archive/overlay-lifts";
import { UserStatus } from "../generated/archive/user-status";
import { NotificationType } from "../generated/archive/notification-type";
import { MessageMatchMode } from "../generated/archive/message-match-mode";
import { TriggerEdge } from "../generated/archive/trigger-edge";
import { BankScope } from "../generated/archive/bank-scope";
import { RecipientKind } from "../generated/archive/recipient-kind";
import { KeyDirection } from "../generated/archive/key-direction";
import { sha256Hex } from "../sha256";
import {
  BANK_LOGICAL_PORT_INT16_MAX,
  BANK_LOGICAL_PORT_INT16_MIN,
} from "../../constants/bankLogicalPort";

/** A registered request body serializer for a specific endpoint. */
export interface RequestSerializer {
  /** Human-readable name for debugging (e.g. "CreateBank"). */
  name: string;
  /** Hex-encoded SHA-256 hash of the .bfbs schema for envelope wrapping. */
  schemaHash: string;
  /**
   * Serialize a plain JavaScript object to FlatBuffer binary.
   * The input is the raw `body` value from the RTK Query mutation call.
   * Returns the packed FlatBuffer bytes (before envelope wrapping).
   */
  pack: (body: unknown) => Uint8Array;
}

function buildBankFloorsFromBody(
  body: Record<string, unknown>,
  bankId: UuidT | null,
  orgId: UuidT | null,
): BankFloorT[] {
  const raw = (dual(body, "bank_floors", "bankFloors") ?? []) as unknown;
  if (!Array.isArray(raw)) return [];
  return raw.map((floor: unknown) => {
    const f = (floor ?? {}) as Record<string, unknown>;
    const t = new BankFloorT();
    t.id = uuidFieldOrZeroWhenAbsent(f, "id");
    t.bankId = uuidField(f, "bank_id") ?? uuidField(f, "bankId") ?? bankId ?? ZERO_UUID;
    t.organizationId =
      uuidField(f, "organization_id") ?? uuidField(f, "organizationId") ?? orgId ?? ZERO_UUID;
    t.name = str(f, "name");
    t.floorNo = num(f, "floor_no", num(f, "floorNo", 0));
    t.floorIndex = num(f, "floor_index", num(f, "floorIndex", 0));
    t.position = num(f, "position", 0);
    t.floorBelowLobby = bool(f, "floor_below_lobby", bool(f, "floorBelowLobby"));
    t.frontDoor = bool(f, "front_door", bool(f, "frontDoor", true));
    t.rearDoor = bool(f, "rear_door", bool(f, "rearDoor"));
    return t;
  });
}

/**
 * Every serializer this module can register.
 *
 * This tuple is the single source of truth: `SerializerName` is derived from
 * it, `SerializerSpec.name` is typed by it, and endpoints reference it through
 * `extraOptions.fbSerializer`. Adding a serializer means adding its name here
 * and a spec below; nothing else in the app needs to know a URL shape.
 */
export const SERIALIZER_NAMES = [
  "CreateOrganization",
  "UpdateOrganization",
  "CreateServer",
  "UpdateServer",
  "CreateAdapter",
  "UpdateAdapter",
  "CreateChannel",
  "UpdateChannel",
  "SetChannelActive",
  "CreateChannelEndpoint",
  "UpdateChannelEndpoint",
  "CreateBank",
  "UpdateBank",
  "CreateLift",
  "UpdateLift",
  "CreateUser",
  "UpdateUser",
  "CreateRole",
  "UpdateRole",
  "CreateKey",
  "UpdateKey",
  "CreateCustomKey",
  "UpdateCustomKey",
  "CreateNotification",
  "UpdateNotification",
  "CreateActionBank",
  "UpdateActionBank",
  "CreateDebounceOverride",
  "UpdateDebounceOverride",
  "CreateTimedAction",
  "UpdateTimedAction",
  "CreateConfiguration",
  "UpdateConfiguration",
  "CreateGroup",
  "UpdateGroup",
  "CreateProtocol",
  "UpdateProtocol",
  "CreateVendor",
  "UpdateVendor",
  "CreateChannelModifier",
  "UpdateChannelModifier",
  "CreateMap",
  "UpdateMap",
  "CreateWaypoint",
  "UpdateWaypoint",
  "CreateGroupWaypoint",
  "UpdateGroupWaypoint",
  "CreateBankWaypoint",
  "UpdateBankWaypoint",
  "SaveSimulationScenario",
] as const;

/** The name of a registered request serializer. */
export type SerializerName = (typeof SERIALIZER_NAMES)[number];

/** The global request serializer registry, keyed by serializer name. */
const registry = new Map<SerializerName, RequestSerializer>();

/**
 * Registers a request body serializer under its declared name.
 *
 * @param name  The serializer's name, as referenced by `extraOptions.fbSerializer`.
 * @param serializer  The serializer to register.
 */
export function registerRequestSerializer(
  name: SerializerName,
  serializer: RequestSerializer,
): void {
  registry.set(name, serializer);
}

/**
 * Looks up a serializer by name.
 * Returns `undefined` before `initializeRequestSerializers()` has resolved.
 */
export function getRequestSerializer(name: SerializerName): RequestSerializer | undefined {
  return registry.get(name);
}

/**
 * Serializes a request body using the named serializer.
 * Returns the LN2E-enveloped FlatBuffer bytes, or `null` if that serializer
 * is not registered yet (indicating the request should fall through to JSON).
 */
export function serializeRequestBody(name: SerializerName, body: unknown): Uint8Array | null {
  const serializer = getRequestSerializer(name);
  if (!serializer) {
    return null;
  }
  const packed = serializer.pack(body);
  return wrapEnvelope(packed, serializer.schemaHash);
}

/**
 * Helper to create a pack function from a `*T` Object API class.
 *
 * @param createT  A function that converts a plain JS object to a `*T` instance.
 * @param finishFn  The generated `finishXxxBuffer` or `finishSizePrefixedXxxBuffer` function.
 */
export function createPacker<T extends { pack(builder: flatbuffers.Builder): flatbuffers.Offset }>(
  createT: (body: unknown) => T,
  finishFn?: (builder: flatbuffers.Builder, offset: flatbuffers.Offset) => void,
): (body: unknown) => Uint8Array {
  return (body: unknown) => {
    const t = createT(body);
    const builder = new flatbuffers.Builder(1024);
    const offset = t.pack(builder);
    if (finishFn) {
      finishFn(builder, offset);
    } else {
      builder.finish(offset);
    }
    return builder.asUint8Array();
  };
}

// ---------------------------------------------------------------------------
// UUID conversion helper
// ---------------------------------------------------------------------------

/**
 * Converts a UUID string (e.g. "550e8400-e29b-41d4-a716-446655440000") to
 * a FlatBuffer UuidT containing upper/lower BigInt halves.
 * Returns null for empty or invalid inputs so optional UUID fields stay null.
 */
// Build the Uuid vector for a body field holding a list of id strings.
//
// Entries that do not parse are DROPPED rather than becoming a null element:
// the schema's vector has no notion of an absent member, so a bad id would
// otherwise ride the wire as a real one and widen the rule's scope to a bank
// nobody chose.
export function uuidListField(body: Record<string, unknown>, key: string): UuidT[] {
  const raw = body[key];
  if (!Array.isArray(raw)) return [];

  return raw
    .map((v) => (typeof v === "string" ? stringToUuidT(v) : null))
    .filter((u): u is UuidT => u !== null);
}

export function stringToUuidT(uuid: string | null | undefined): UuidT | null {
  if (!uuid) return null;
  const hex = uuid.replace(/-/g, "");
  if (hex.length !== 32) return null;
  const upper = BigInt(`0x${hex.slice(0, 16)}`);
  const lower = BigInt(`0x${hex.slice(16, 32)}`);
  return new UuidT(new U128T(upper, lower));
}

/** Zero UUID placeholder for POST (create) requests where the server assigns the ID. */
const ZERO_UUID = new UuidT(new U128T(BigInt(0), BigInt(0)));

function requiredUuidField(body: Record<string, unknown>, key: string): UuidT {
  return uuidField(body, key) ?? stringToUuidT(str(body, key)) ?? ZERO_UUID;
}

function uuidFieldOrZeroWhenAbsent(body: Record<string, unknown>, key: string): UuidT | null {
  const raw = body[key];
  if (raw === undefined || raw === null) {
    return ZERO_UUID;
  }

  return uuidField(body, key);
}

// ---------------------------------------------------------------------------
// Enum forward-mapping helpers (string → FlatBuffer enum)
// ---------------------------------------------------------------------------

function toOrgType(v: string | undefined): OrgType {
  const map: Record<string, OrgType> = {
    MASTER: OrgType.MASTER,
    ORG: OrgType.ORG,
    SUBORG: OrgType.SUBORG,
  };
  return map[v ?? ""] ?? OrgType.ORG;
}

function toOrgStatus(v: string | undefined): OrgStatus {
  return v === "DRAFT" ? OrgStatus.DRAFT : OrgStatus.ACTIVE;
}

export function toLiftType(v: string | number | undefined): LiftType {
  if (typeof v === "number") return v as LiftType;
  const map: Record<string, LiftType> = {
    ELEVATOR: LiftType.ELEVATOR,
    ESCALATOR: LiftType.ESCALATOR,
    MOVINGWALK: LiftType.MOVINGWALK,
    BOT: LiftType.BOT,
    GPIO: LiftType.GPIO,
  };
  return map[v ?? ""] ?? LiftType.ELEVATOR;
}

export function toChannelType(v: string | undefined): ChannelType {
  const map: Record<string, ChannelType> = {
    SERIAL: ChannelType.SERIAL,
    TCP: ChannelType.TCP,
    UDP: ChannelType.UDP,
    ADAPTER: ChannelType.ADAPTER,
    TCP_BOT: ChannelType.TCP_BOT,
    HTTP: ChannelType.HTTP,
  };
  return map[v ?? ""] ?? ChannelType.TCP;
}

export function toChannelMode(v: string | number | undefined): ChannelMode {
  if (typeof v === "number") return v as ChannelMode;
  const map: Record<string, ChannelMode> = {
    SINGLE: ChannelMode.SINGLE,
    FAILOVER: ChannelMode.FAILOVER,
    MERGE: ChannelMode.MERGE,
    MIXED: ChannelMode.MIXED,
    LOAD_BALANCE: ChannelMode.LOAD_BALANCE,
  };
  return map[v ?? ""] ?? ChannelMode.SINGLE;
}

export function toMergePolicy(v: string | number | undefined): MergePolicy {
  if (typeof v === "number") return v as MergePolicy;
  const map: Record<string, MergePolicy> = {
    LAST_WRITE_WINS: MergePolicy.LAST_WRITE_WINS,
    PRIMARY_PREFERRED: MergePolicy.PRIMARY_PREFERRED,
    QUORUM: MergePolicy.QUORUM,
  };
  return map[v ?? ""] ?? MergePolicy.LAST_WRITE_WINS;
}

export function toAdapterType(v: string | undefined): AdapterType {
  const map: Record<string, AdapterType> = {
    REST: AdapterType.REST,
    GRAPHQL: AdapterType.GRAPHQL,
    GRPC: AdapterType.GRPC,
    SOAP: AdapterType.SOAP,
    WEBSOCKET: AdapterType.WEBSOCKET,
    TCP: AdapterType.TCP,
  };
  return map[v ?? ""] ?? AdapterType.REST;
}

export function toAdapterProtocol(v: string | undefined): AdapterProtocol {
  const map: Record<string, AdapterProtocol> = {
    MCE: AdapterProtocol.MCE,
    MODBUS: AdapterProtocol.MODBUS,
    BEAGLE: AdapterProtocol.BEAGLE,
    LN1: AdapterProtocol.LN1,
    LN2: AdapterProtocol.LN2,
    DILIGENT: AdapterProtocol.DILIGENT,
    BACNET: AdapterProtocol.BACNET,
    RVU: AdapterProtocol.RVU,
    OPC: AdapterProtocol.OPC,
    OPCUA: AdapterProtocol.OPCUA,
    SEQUENT: AdapterProtocol.SEQUENT,
    NUMATOGPIO: AdapterProtocol.NUMATOGPIO,
    NUMATORELAY: AdapterProtocol.NUMATORELAY,
    STM32: AdapterProtocol.STM32,
    CGA: AdapterProtocol.CGA,
    LOBBYVISION: AdapterProtocol.LOBBYVISION,
    LOBBYVISION2: AdapterProtocol.LOBBYVISION2,
    CANELEVATOR: AdapterProtocol.CANELEVATOR,
  };
  return map[v ?? ""] ?? AdapterProtocol.MCE;
}

function toAdapterAuth(v: string | undefined): AdapterAuth {
  const map: Record<string, AdapterAuth> = {
    NONE: AdapterAuth.NONE,
    APIKEY: AdapterAuth.APIKEY,
    PASSWORD: AdapterAuth.PASSWORD,
    JWT: AdapterAuth.JWT,
    OAUTH: AdapterAuth.OAUTH,
  };
  return map[v ?? ""] ?? AdapterAuth.NONE;
}

/**
 * Resolve a vendor floor indexing value to its generated enum member.
 *
 * The wire carries this three ways — the FlatBuffer integer, the JSON
 * `one_based`/`zero_based` spelling, and the enum's own member name — so
 * anything that hydrates an adapter has to normalise before it can compare or
 * render. Exported rather than copied, because a second map is a second thing
 * to forget when the schema gains a member.
 */
export function toVendorFloorIndexing(v: string | number | undefined): VendorFloorIndexing {
  if (typeof v === "number") return v as VendorFloorIndexing;
  const map: Record<string, VendorFloorIndexing> = {
    one_based: VendorFloorIndexing.ONE_BASED,
    zero_based: VendorFloorIndexing.ZERO_BASED,
    ONE_BASED: VendorFloorIndexing.ONE_BASED,
    ZERO_BASED: VendorFloorIndexing.ZERO_BASED,
  };
  return map[v ?? ""] ?? VendorFloorIndexing.ONE_BASED;
}

function toUserStatus(v: string | undefined): UserStatus {
  const map: Record<string, UserStatus> = {
    NEW: UserStatus.NEW,
    ACTIVE: UserStatus.ACTIVE,
    OFFLINE_CREATED: UserStatus.OFFLINE_CREATED,
    OFFLINE_SYNCED: UserStatus.OFFLINE_SYNCED,
    OFFLINE_ACTIVE: UserStatus.OFFLINE_ACTIVE,
    BLOCKED: UserStatus.BLOCKED,
    DELETED: UserStatus.DELETED,
  };
  return map[v ?? ""] ?? UserStatus.ACTIVE;
}

/**
 * Coerce a body value into ActionFiringMode.
 *
 * `def` is REQUIRED rather than defaulted, because the two call sites need
 * DIFFERENT absent-key fallbacks and each must match its own schema default:
 * Organization defaults TWO_STEP, User defaults INHERIT. A single shared
 * fallback would put one of them out of parity with its schema — the exact
 * discrepancy DEFAULT_PARITY_EXEMPTIONS already records for
 * doorUnknownAsOpening, and there is no reason to add a second.
 *
 * Every member is mapped by name. A map that silently falls through is how
 * ON_OFF and ONE_SHOT were written to rows as TOGGLE for months.
 *
 * The numeric branch delegates to utils/actionFiringMode's own
 * toActionFiringMode for VALIDATION rather than re-implementing it: that
 * shared helper only accepts a number that is actually ONE_CLICK, TWO_STEP,
 * or (by construction, since it's the fallback returned for anything else)
 * INHERIT, so `shared(v) === v` is true precisely when `v` is one of those
 * three ordinals — true independent of the enum's underlying numeric values,
 * not a coincidence tied to INHERIT being 0. An out-of-range ordinal (a stale
 * client, or a future enum member this build doesn't know yet) must not be
 * packed through as-is, or LiftCloud receives a wire value it doesn't
 * expect; it falls back to `def` here rather than to the shared helper's own
 * INHERIT fallback, because `def` is what keeps this call site's schema
 * parity (see above) — an invalid Organization value must fall back to
 * TWO_STEP, not silently become INHERIT.
 */
function toActionFiringMode(
  v: string | number | undefined,
  def: ActionFiringMode,
): ActionFiringMode {
  if (typeof v === "number") {
    return toValidatedActionFiringMode(v as ActionFiringMode) === v ? (v as ActionFiringMode) : def;
  }
  if (v === undefined || v === null || v === "") return def;

  const map: Record<string, ActionFiringMode> = {
    INHERIT: ActionFiringMode.INHERIT,
    TWO_STEP: ActionFiringMode.TWO_STEP,
    ONE_CLICK: ActionFiringMode.ONE_CLICK,
  };

  return map[v] ?? def;
}

/**
 * An organization's actionFiringMode from a request body — under either key.
 *
 * The caller may be a snake_case request body (the wizard's orgBodyOf) or a
 * GET-decoded, camelCase Organization object sent back as-is (Organization
 * Management's edit form): either way the value must reach here, or this
 * packs the TWO_STEP default over whatever was actually configured. One
 * function so CreateOrganization and UpdateOrganization can't drift on it.
 */
function resolveOrganizationActionFiringMode(body: Record<string, unknown>): ActionFiringMode {
  return toActionFiringMode(
    (body.action_firing_mode ?? body.actionFiringMode) as string | number | undefined,
    ActionFiringMode.TWO_STEP,
  );
}

function toActionButtonType(v: string | number | undefined): ActionButtonType {
  if (typeof v === "number") return v as ActionButtonType;
  // Every member of ActionButtonType, deliberately: a value missing here does
  // not fail, it falls through to the TOGGLE default and is written to the row
  // as TOGGLE. ON_OFF and ONE_SHOT were absent while both were selectable in
  // the wizard, so picking either saved TOGGLE instead and the operator's
  // choice was unrecoverable by editing. Adding a schema value means adding it
  // here; buttonTypeRoundTrip.test.ts fails if this map stops being complete.
  const map: Record<string, ActionButtonType> = {
    TOGGLE: ActionButtonType.TOGGLE,
    HOLDBACK: ActionButtonType.HOLDBACK,
    TURNBACK: ActionButtonType.TURNBACK,
    TOGGLETIMER: ActionButtonType.TOGGLETIMER,
    ENERGIZER_TURN_BACK: ActionButtonType.ENERGIZER_TURN_BACK,
    SWITCH: ActionButtonType.SWITCH,
    ON_OFF: ActionButtonType.ON_OFF,
    ONE_SHOT: ActionButtonType.ONE_SHOT,
    // SINGLE is a distinct wire value (7), not an alias of SWITCH (5). The
    // wizard deliberately offers only SWITCH and maps its SINGLE preset onto
    // it, but a row ALREADY stored as SINGLE passes back through here on any
    // re-serialize -- and without this entry it was rewritten to TOGGLE.
    SINGLE: ActionButtonType.SINGLE,
  };
  return map[v ?? ""] ?? ActionButtonType.TOGGLE;
}

/**
 * Coerce a body[key] field into a string[]. Accepts string[] (returned
 * as-is), nullish (returned []), or a single string (wrapped). Used by
 * the ActionBank serializer for valid_states / toggle_valid_states.
 */
function strArray(body: Record<string, unknown>, key: string): string[] {
  const v = body[key];
  if (v == null) return [];
  if (Array.isArray(v)) return v.map((s) => String(s));
  if (typeof v === "string") return [v];
  return [];
}

// The widening enums arrive as their FlatBuffer NAMES. An unknown or absent
// name resolves to the enum's zero value, which is deliberately the behaviour a
// rule had before the field existed — CATEGORY never read message_value,
// ACTIVATED matches the offline path's long-standing requirement, and LIST is
// single-bank. So an old client omitting them produces an unchanged rule.
function toMatchMode(v: string | undefined): MessageMatchMode {
  const map: Record<string, MessageMatchMode> = {
    CATEGORY: MessageMatchMode.CATEGORY,
    EXACT: MessageMatchMode.EXACT,
    ANY: MessageMatchMode.ANY,
  };
  return map[v ?? ""] ?? MessageMatchMode.CATEGORY;
}

function toTriggerEdge(v: string | undefined): TriggerEdge {
  const map: Record<string, TriggerEdge> = {
    ACTIVATED: TriggerEdge.ACTIVATED,
    CLEARED: TriggerEdge.CLEARED,
    BOTH: TriggerEdge.BOTH,
  };
  return map[v ?? ""] ?? TriggerEdge.ACTIVATED;
}

function toBankScope(v: string | undefined): BankScope {
  const map: Record<string, BankScope> = {
    LIST: BankScope.LIST,
    ALL_IN_ORG: BankScope.ALL_IN_ORG,
  };
  return map[v ?? ""] ?? BankScope.LIST;
}

function toRecipientKind(v: string | undefined): RecipientKind {
  const map: Record<string, RecipientKind> = {
    ADDRESS: RecipientKind.ADDRESS,
    USER: RecipientKind.USER,
    ROLE: RecipientKind.ROLE,
  };
  return map[v ?? ""] ?? RecipientKind.ADDRESS;
}

function toNotificationType(v: string | undefined): NotificationType {
  const map: Record<string, NotificationType> = {
    SMS: NotificationType.SMS,
    EMAIL: NotificationType.EMAIL,
    WHATSAPP: NotificationType.WHATSAPP,
  };
  return map[v ?? ""] ?? NotificationType.EMAIL;
}

// ---------------------------------------------------------------------------
// Convenience: extract a string field from a plain JS body object
// ---------------------------------------------------------------------------
function str(body: Record<string, unknown>, key: string): string {
  const v = body[key];
  if (v == null) return "";
  if (typeof v === "string") return v;
  // Handle UuidT or other FlatBuffer object values (e.g. when wizard state stores
  // a UuidT instead of a normalized string). formatUuidT returns "" for invalid values.
  if (typeof v === "object") return formatUuidT(v as Parameters<typeof formatUuidT>[0]);
  return String(v);
}

function bool(body: Record<string, unknown>, key: string, def = false): boolean {
  const v = body[key];
  if (typeof v === "boolean") return v;
  return def;
}

function num(body: Record<string, unknown>, key: string, def = 0): number {
  const v = body[key];
  if (typeof v === "number") return v;
  return def;
}

/** Callers pass the device type either as its name ("ESCALATOR") or as the enum value (1). */
function liftTypeField(body: Record<string, unknown>, key: string): LiftType {
  const v = body[key];
  return typeof v === "number" || typeof v === "string" ? toLiftType(v) : toLiftType(undefined);
}

/**
 * Reads a bank body's `logical_port` under either spelling and refuses a value
 * the wire cannot carry.
 *
 * `archive/bank.fbs` declares the field a `short`, and the generated builder
 * writes it with `addFieldInt16` -> `DataView.setInt16`, which wraps instead of
 * failing: 40000 goes out as -25536, 32768 as -32768. Every wrapped value lands
 * on another VALID port, so nothing downstream can tell the bank was corrupted.
 * The bank forms cap the input at the same bound; throwing here is the backstop
 * for the callers that do not (import flows, scripts). The base query turns a
 * throw from `pack` into a CUSTOM_ERROR result, so the save fails visibly
 * instead of storing the wrong port.
 *
 * `!(in range)` rather than `out of range` so NaN is refused too -- it would
 * pack as 0.
 */
function bankLogicalPort(body: Record<string, unknown>): number {
  const port = num(body, "logical_port", num(body, "logicalPort", 0));
  if (!(port >= BANK_LOGICAL_PORT_INT16_MIN && port <= BANK_LOGICAL_PORT_INT16_MAX)) {
    throw new Error(
      `Bank logical_port ${port} is outside the int16 range ${BANK_LOGICAL_PORT_INT16_MIN} to ${BANK_LOGICAL_PORT_INT16_MAX} that the wire format can carry`,
    );
  }
  return port;
}

/**
 * Reads a body field under either its snake_case or camelCase spelling,
 * preferring snake_case. Returns the raw (possibly undefined) value —
 * callers that need type coercion or a default still do it themselves,
 * exactly as they did before this replaced their inline
 * `body.snake_key ?? body.camelKey`.
 *
 * Centralizes the dual-spelling read so a call site cannot list the two
 * keys in the wrong precedence order, or diverge from every other site's
 * snake-case-first convention.
 */
function dual(body: Record<string, unknown>, snakeKey: string, camelKey: string): unknown {
  return body[snakeKey] ?? body[camelKey];
}

/**
 * Reads an OPC vendor path segment under either spelling and trims it.
 *
 * FlatBuffers cannot express a null string, so "" on the wire means UNSET —
 * which is why the Go side guards these fields rather than clearing on absent.
 * Trimming here keeps a whitespace-only entry from arriving as a "configured"
 * segment that would expand to a path matching nothing on the OPC server.
 */
function vendorPath(body: Record<string, unknown>, snakeKey: string, camelKey: string): string {
  const v = dual(body, snakeKey, camelKey);

  return typeof v === "string" ? v.trim() : "";
}

/**
 * Reads the OPC vendor-paths presence flag.
 *
 * The Setup Wizard and the standalone bank form round-trip all three bank
 * segments on every save, and the Lifts & Floors step round-trips both lift
 * segments, whenever their inputs are actually rendered — so those callers send
 * `true` here to mark themselves authoritative. That lets the server distinguish
 * "the user cleared this segment" (true + empty string → write NULL) from "this
 * client never models the field" (false → guarded-preserve, stored columns
 * untouched). Legacy clients that don't know the flag send nothing and the server
 * sees false.
 *
 * Read the same way for `Archive.Bank` and `Archive.Lift`: the two tables carry
 * the field under the same name, and a caller that does not render the inputs
 * omits it entirely rather than sending `false`.
 *
 * Accepts both spellings and the JSON boolean `true` or the string `"true"`
 * (RTK Query passes the value through verbatim, and the FlatBuffer builder
 * expects a plain boolean).
 */
function vendorPathsProvided(body: Record<string, unknown>): boolean {
  const v = dual(body, "vendor_paths_provided", "vendorPathsProvided");

  return v === true || v === "true";
}

/**
 * Reads the bank-overlay presence flag, exactly as `vendorPathsProvided` reads
 * the vendor-path one.
 *
 * The bank editors that render the "Draw on bank" controls (the Setup Wizard's
 * Banks step and the standalone bank form) send `true` on every save, which
 * makes the save authoritative over `overlay_bank_id` and `overlay_lifts`: an
 * absent id then CLEARS the overlay. Every other bank save — the Lifts & Floors
 * step's bank PUT, imports, a client that predates overlays — sends nothing,
 * reads as false here, and LiftCloud leaves the stored overlay untouched.
 */
function overlayProvided(body: Record<string, unknown>): boolean {
  const v = dual(body, "overlay_provided", "overlayProvided");

  return v === true || v === "true";
}

/**
 * The bank this bank draws on, or null for "no overlay".
 *
 * `optionalUuidField` rather than `uuidField`: a zero UUID names no bank, and
 * sending it as a target would ask LiftCloud to draw on a bank that does not
 * exist instead of clearing the overlay.
 */
function overlayBankIdField(body: Record<string, unknown>): UuidT | null {
  return optionalUuidField(body, "overlay_bank_id") ?? optionalUuidField(body, "overlayBankId");
}

/**
 * Which of the target bank's lifts the overlay draws on, as the generated enum.
 *
 * Accepts the enum value (what the editors and a decoded `BankT` carry) or its
 * member name; absent means the schema default, `ALL_LIFTS`. Anything else
 * throws rather than defaulting: the field is a uint8 on the wire, so an unknown
 * number would pack as itself and reach VTS as a mode nobody implements, and
 * quietly turning it into `ALL_LIFTS` would draw on every car when the operator
 * asked for something else. The base query turns the throw into a failed save.
 * Resolved through the enum's own reverse mapping, so a member appended to the
 * schema is accepted without touching this function.
 */
function overlayLiftsField(body: Record<string, unknown>): OverlayLifts {
  const v = dual(body, "overlay_lifts", "overlayLifts");
  if (v == null) return OverlayLifts.ALL_LIFTS;
  const memberName = typeof v === "string" ? v : OverlayLifts[v as OverlayLifts];
  if (
    typeof memberName === "string" &&
    Object.prototype.hasOwnProperty.call(OverlayLifts, memberName)
  ) {
    const value: unknown = OverlayLifts[memberName as keyof typeof OverlayLifts];
    if (typeof value === "number") return value as OverlayLifts;
  }
  throw new Error(`Bank overlay_lifts ${String(v)} is not a value the OverlayLifts enum defines`);
}

/**
 * Reads a body field under either spelling (see `dual()`) as a base64
 * string and decodes it to raw bytes, for FlatBuffer fields with no JSON
 * twin (`cdo_binary`, `opc_ua_node_lift_mapping_binary`). Returns undefined
 * for absent, empty, or non-string input — every existing base64→bytes call
 * site guarded on exactly that before this was extracted, so a caller
 * assigns the target field only when this returns something.
 */
function base64BytesField(
  body: Record<string, unknown>,
  snakeKey: string,
  camelKey: string,
): number[] | undefined {
  const raw = dual(body, snakeKey, camelKey);
  if (typeof raw !== "string" || raw === "") return undefined;
  return Array.from(atob(raw), (c) => c.charCodeAt(0));
}

function nullable(body: Record<string, unknown>, key: string): string | null {
  const v = body[key];
  if (v == null) return null;
  return String(v);
}

/**
 * `nullable()` for a field that callers may spell either way.
 *
 * A single-key `nullable()` read cannot tell "the operator cleared this" from
 * "this caller names the field under the other spelling" — both read as an
 * absent key and pack as null, and on the FlatBuffer path the server cannot
 * distinguish an absent string from an empty one, so it writes the column
 * blank. Every dual-spelled field in this file therefore goes through
 * `dual()`; a nullable string is no exception.
 *
 * Semantics are `dual()`'s precedence (snake_case first) combined with
 * `nullable()`'s value handling: absent or null in both spellings is null,
 * an explicit "" stays "" so a deliberate clear still reaches the wire.
 */
function dualNullable(
  body: Record<string, unknown>,
  snakeKey: string,
  camelKey: string,
): string | null {
  const v = dual(body, snakeKey, camelKey);
  if (v == null) return null;
  return String(v);
}

/**
 * Read the rows out of a body's repeated-table field.
 *
 * `required` names the fields a row is useless without — a netdef with no id,
 * a VLAN with no link. It no longer refers to a schema attribute: `required`
 * came off every field in net_config.fbs under review, because it means
 * "present in the vtable" rather than "non-empty" (an empty string packs and
 * verifies) and because Go's builder does not enforce it while TypeScript's
 * and Rust's do. So nothing throws now; these rows are simply meaningless
 * downstream, and dropping one is better than sending the edge a netdef it
 * will name after the empty string. The form blocks saving while such a row
 * exists (`siteNetworkProblems`), so reaching here means the body came from
 * somewhere that skipped that check.
 */
function rowsOf(value: unknown, key: string, required: string[]): Record<string, unknown>[] {
  const container = value as Record<string, unknown> | null | undefined;
  const raw = container ? container[key] : undefined;
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((row): row is Record<string, unknown> => !!row && typeof row === "object")
    .filter((row) => required.every((field) => !!nullable(row, field)));
}

/** A repeated string field: absent, a single string, or a list. */
function strListField(row: Record<string, unknown>, key: string): string[] {
  const raw = row[key];
  if (Array.isArray(raw)) return raw.map((item) => String(item)).filter(Boolean);
  if (typeof raw === "string" && raw !== "") return [raw];
  return [];
}

/**
 * Read the router-advertisement setting as the three-valued enum it is.
 *
 * Three values because the edge's renderer has three behaviours: emit
 * `accept-ra: true`, emit `accept-ra: false`, or emit nothing so the kernel
 * default stands. Absent must therefore mean UNSET rather than DISABLED —
 * collapsing it would turn every untouched form into an explicit refusal and
 * cost a SLAAC site its IPv6 default route at first boot.
 *
 * Accepts a boolean as well, because that is what the form held before this
 * was an enum and a draft saved then is still a draft. A legacy `false` is
 * not the same absence: it is what that old boolean form wrote when someone
 * deliberately unchecked accept-ra, and it must land on DISABLED — folding
 * it into UNSET the way a genuinely absent value does would silently turn a
 * deliberate refusal back into the kernel default.
 */
function acceptRaField(row: Record<string, unknown>): AcceptRa {
  const raw = row.accept_ra ?? row.acceptRa;

  if (raw === true) return AcceptRa.ENABLED;
  if (raw === false) return AcceptRa.DISABLED;
  if (raw == null) return AcceptRa.UNSET;
  if (raw === AcceptRa.ENABLED || raw === AcceptRa.DISABLED) return raw;

  return AcceptRa.UNSET;
}

/**
 * A scalar the schema stores as an unsigned integer.
 *
 * Clamped, not just truncated. The FlatBuffers JS builder writes straight into
 * a Uint8Array with no range check, so an out-of-range value wraps silently: a
 * VLAN tag typed as 70000 reached the edge as 4464 — a valid tag on a
 * different network, and plausible enough to survive a config review. The UI
 * bounds its inputs, but this is the other caller and it takes whatever a
 * request body carries.
 */
function intField(row: Record<string, unknown>, key: string, max = 0xffffffff): number {
  const raw = row[key];
  const n = typeof raw === "number" ? raw : Number(raw);
  if (!Number.isFinite(n)) return 0;

  return Math.min(Math.max(Math.trunc(n), 0), max);
}

/**
 * Build the server's site network from the request body.
 *
 * Returns null when the body carries no `site_network`, and that is the point:
 * the schema field is a nullable table, and a server whose network is null is
 * one the edge leaves alone at first boot. Emitting an empty SiteNetwork
 * instead would tell the edge to apply a network with no interfaces in it —
 * on a headless box, that is the difference between "unchanged" and "off the
 * network with no way back".
 *
 * Every field is read defensively rather than cast: this body comes from a
 * form, and a half-filled row is normal while someone is still typing.
 */
function siteNetworkField(body: Record<string, unknown>): SiteNetworkT | null {
  const raw = body.site_network ?? body.siteNetwork;
  if (!raw || typeof raw !== "object") return null;

  const t = new SiteNetworkT();

  // Each row is constructed empty and assigned by name. A positional call to a
  // generated *T compiles fine after a field is dropped from the schema — every
  // argument after the hole silently lands one slot early — and `NicConfig` has
  // thirteen of them. The repo's field-coverage guard enforces this.
  t.nics = rowsOf(raw, "nics", ["id"]).map((row) => {
    const nic = new NicConfigT();
    nic.id = nullable(row, "id");
    nic.mac = nullable(row, "mac");
    nic.name = nullable(row, "name");
    nic.setName = nullable(row, "set_name");
    nic.role = nullable(row, "role");
    nic.optional = !!row.optional;
    nic.dhcp4 = !!row.dhcp4;
    nic.dhcp6 = !!row.dhcp6;
    nic.acceptRa = acceptRaField(row);
    nic.addresses = strListField(row, "addresses");
    nic.gateway4 = nullable(row, "gateway4");
    nic.gateway6 = nullable(row, "gateway6");
    nic.mtu = intField(row, "mtu", 0xffffffff);
    return nic;
  });

  t.bonds = rowsOf(raw, "bonds", ["id"]).map((row) => {
    const bond = new BondConfigT();
    bond.id = nullable(row, "id");
    bond.members = strListField(row, "members");
    bond.mode = nullable(row, "mode");
    bond.lacpRate = nullable(row, "lacp_rate");
    bond.hashPolicy = nullable(row, "hash_policy");
    bond.miiInterval = intField(row, "mii_interval");
    bond.upDelay = intField(row, "up_delay");
    bond.downDelay = intField(row, "down_delay");
    bond.minLinks = intField(row, "min_links");
    bond.primary = nullable(row, "primary");
    bond.dhcp4 = !!row.dhcp4;
    bond.dhcp6 = !!row.dhcp6;
    bond.acceptRa = acceptRaField(row);
    bond.addresses = strListField(row, "addresses");
    bond.gateway4 = nullable(row, "gateway4");
    bond.gateway6 = nullable(row, "gateway6");
    bond.mtu = intField(row, "mtu");
    return bond;
  });

  t.vlans = rowsOf(raw, "vlans", ["id", "link"]).map((row) => {
    const vlan = new VlanConfigT();
    vlan.id = nullable(row, "id");
    vlan.tag = intField(row, "tag", 0xffff);
    vlan.link = nullable(row, "link");
    vlan.dhcp4 = !!row.dhcp4;
    vlan.dhcp6 = !!row.dhcp6;
    vlan.acceptRa = acceptRaField(row);
    vlan.addresses = strListField(row, "addresses");
    vlan.gateway4 = nullable(row, "gateway4");
    vlan.gateway6 = nullable(row, "gateway6");
    vlan.mtu = intField(row, "mtu");
    return vlan;
  });

  t.routes = rowsOf(raw, "routes", ["on", "to"]).map((row) => {
    const route = new RouteConfigT();
    route.on = nullable(row, "on");
    route.to = nullable(row, "to");
    route.via = nullable(row, "via");
    route.tableId = intField(row, "table_id");
    route.metric = intField(row, "metric");
    route.onLink = !!row.on_link;
    return route;
  });

  t.rules = rowsOf(raw, "rules", ["on"]).map((row) => {
    const rule = new RuleConfigT();
    rule.on = nullable(row, "on");
    rule.from = nullable(row, "from");
    rule.to = nullable(row, "to");
    rule.tableId = intField(row, "table_id");
    rule.priority = intField(row, "priority");
    rule.mark = intField(row, "mark");
    return rule;
  });

  const container = raw as Record<string, unknown>;
  t.probeTarget = nullable(container, "probe_target");
  t.allowedCidrs = strListField(container, "allowed_cidrs");
  t.nameservers = strListField(container, "nameservers");
  t.searchDomains = strListField(container, "search_domains");

  return t;
}

/**
 * Extracts a JSON field from the body: if the value is an object/array, JSON.stringify it.
 * If the value is already a string, return it as-is. Returns null when absent or null.
 *
 * BigInt values are coerced to Number during stringify — plain `JSON.stringify`
 * throws "Do not know how to serialize a BigInt" on them. JSON-blob body fields
 * legitimately carry BigInts (e.g. a MODBUS adapter's `cdo_template.pollingInterval`,
 * whose editor type is `bigint`), so cloning/saving such an adapter would otherwise
 * fail here with "Adapter not saved correctly. Do not know how to serialize a
 * BigInt." (LIFT-186). Number is the correct target: these are small counters /
 * intervals the backend unmarshals as numbers, matching AdapterEditor's own
 * bigint→Number conversion for `cdo_template`.
 */
function jsonField(body: Record<string, unknown>, key: string): string | null {
  const v = body[key];
  if (v == null) return null;
  if (typeof v === "string") return v;
  return JSON.stringify(v, (_k, value) => (typeof value === "bigint" ? Number(value) : value));
}

/**
 * Reads a UuidT | null field directly (camelCase interface fields).
 * Falls through to stringToUuidT for plain string ids.
 *
 * Handles structuredClone artifacts: cloning a UuidT instance produces a POJO
 * with the correct shape but no prototype, so `instanceof UuidT` is false and
 * calling `.pack()` later throws. When we detect a plain object, round-trip
 * through formatUuidT (property-access only, works on POJOs) → stringToUuidT
 * to reconstruct a real UuidT instance with a working pack() method.
 */
function uuidField(body: Record<string, unknown>, key: string): UuidT | null {
  const v = body[key];
  if (v == null) return null;
  if (v instanceof UuidT) return v;
  if (typeof v === "object") {
    const uuidStr = formatUuidT(v as Parameters<typeof formatUuidT>[0]);
    return uuidStr ? stringToUuidT(uuidStr) : null;
  }
  return stringToUuidT(String(v));
}

/**
 * Reads a numeric enum field directly (already an integer on camelCase interfaces).
 */
function intEnum(body: Record<string, unknown>, key: string, def = 0): number {
  const v = body[key];
  if (typeof v === "number") return v;
  return def;
}

/**
 * Reads a pre-constructed FlatBuffer MessageT from `body.payload`.
 * Callers build the typed MessageT at the form layer; this passes it through.
 * Returns null when absent or not a MessageT instance.
 */
function messageTField(body: Record<string, unknown>): MessageT | null {
  const v = body.payload;
  if (v instanceof MessageT) return v;
  return null;
}

/**
 * Reads `body[key]` as a list the form layer built with a generated Object API
 * class, and passes it through to be packed, as `payload` passes a MessageT.
 * Absent or null reads as none. Anything else is refused rather than copied
 * field by field: a hand-described row keeps compiling after the schema
 * changes, so copying one would hide the very drift the class exists to catch,
 * and dropping it would clear the list on every PUT.
 */
function objectApiList<T>(
  body: Record<string, unknown>,
  key: string,
  ctor: new (...args: never[]) => T,
  typeName: string,
): T[] {
  const v = body[key];
  if (v == null) return [];
  if (Array.isArray(v) && v.every((e): e is T => e instanceof ctor)) return v;
  throw new TypeError(`${key} must be a list of ${typeName}`);
}

/**
 * `body.expectedSequence`: the steps (schema v5, v7, v8) the form built as
 * ModifierSequenceStepT. Shared by the POST and PUT ChannelModifier
 * serializers so create and update cannot read it differently.
 */
function expectedSequenceField(body: Record<string, unknown>): ModifierSequenceStepT[] {
  return objectApiList(body, "expectedSequence", ModifierSequenceStepT, "ModifierSequenceStepT");
}

/**
 * `body.preconditions`: the schema-v8 conditions the form built as
 * ModifierStateConditionT, shared like `expectedSequenceField`. The schema
 * requires `channelId`, so a condition without one fails at pack time, as a
 * modifier without a source channel does.
 */
function preconditionsField(body: Record<string, unknown>): ModifierStateConditionT[] {
  return objectApiList(body, "preconditions", ModifierStateConditionT, "ModifierStateConditionT");
}

/**
 * Reads an optional UUID reference, treating "", absent and the all-zero UUID
 * alike as "not set" (null). The schema documents an absent or zero
 * `observe_channel_id` as "the target channel", and the backend stores a
 * non-null one as a real foreign key — so a zero UUID must never reach the wire
 * as if it named a channel.
 */
function optionalUuidField(body: Record<string, unknown>, key: string): UuidT | null {
  const u = uuidField(body, key);
  if (u === null || u.value === null) return null;
  return u.value.upper === BigInt(0) && u.value.lower === BigInt(0) ? null : u;
}

const toWaypointT = (raw: unknown): WaypointT => {
  const body = (raw ?? {}) as Record<string, unknown>;
  const waypoint = new WaypointT();
  waypoint.id = uuidField(body, "id");
  waypoint.nickname = nullable(body, "nickname") ?? nullable(body, "text");
  waypoint.x = num(body, "x", 0);
  waypoint.y = num(body, "y", 0);
  waypoint.backgroundColorHex =
    nullable(body, "background_color_hex") ?? nullable(body, "backgroundColorHex");
  waypoint.backgroundOpacity = num(body, "background_opacity", num(body, "backgroundOpacity", 0));
  waypoint.backgroundWidth = num(body, "background_width", num(body, "backgroundWidth", 0));
  waypoint.backgroundHeight = num(body, "background_height", num(body, "backgroundHeight", 0));
  waypoint.displayName = bool(body, "display_name", bool(body, "displayName", false));
  waypoint.fontSize = num(body, "font_size", num(body, "fontSize", 0));
  waypoint.fontColorHex = nullable(body, "font_color_hex") ?? nullable(body, "fontColorHex");
  waypoint.backgroundRotationDeg = num(
    body,
    "background_rotation_deg",
    num(body, "backgroundRotationDeg", 0),
  );
  waypoint.iconRotationDeg = num(body, "icon_rotation_deg", num(body, "iconRotationDeg", 0));
  waypoint.nameRotationDeg = num(body, "name_rotation_deg", num(body, "nameRotationDeg", 0));
  waypoint.syncRotations = bool(body, "sync_rotations", bool(body, "syncRotations", true));
  waypoint.iconWidth = num(body, "icon_width", num(body, "iconWidth", 0));
  waypoint.iconHeight = num(body, "icon_height", num(body, "iconHeight", 0));
  waypoint.maintainIconAspectRatio = bool(
    body,
    "maintain_icon_aspect_ratio",
    bool(body, "maintainIconAspectRatio", true),
  );
  waypoint.linkedLift = nullable(body, "linked_lift") ?? nullable(body, "linkedLift");
  return waypoint;
};

const toGroupWaypointT = (raw: unknown): GroupWaypointT => {
  const body = (raw ?? {}) as Record<string, unknown>;
  const groupWaypoint = new GroupWaypointT();
  groupWaypoint.id = uuidField(body, "id");
  groupWaypoint.nickname = nullable(body, "nickname");
  groupWaypoint.x = num(body, "x", 0);
  groupWaypoint.y = num(body, "y", 0);
  groupWaypoint.backgroundColorHex =
    nullable(body, "background_color_hex") ?? nullable(body, "backgroundColorHex");
  groupWaypoint.backgroundOpacity = num(
    body,
    "background_opacity",
    num(body, "backgroundOpacity", 0),
  );
  groupWaypoint.backgroundWidth = num(body, "background_width", num(body, "backgroundWidth", 0));
  groupWaypoint.backgroundHeight = num(body, "background_height", num(body, "backgroundHeight", 0));
  groupWaypoint.displayName = bool(body, "display_name", bool(body, "displayName", false));
  groupWaypoint.fontSize = num(body, "font_size", num(body, "fontSize", 0));
  groupWaypoint.fontColorHex = nullable(body, "font_color_hex") ?? nullable(body, "fontColorHex");
  groupWaypoint.backgroundRotationDeg = num(
    body,
    "background_rotation_deg",
    num(body, "backgroundRotationDeg", 0),
  );
  groupWaypoint.nameRotationDeg = num(body, "name_rotation_deg", num(body, "nameRotationDeg", 0));
  groupWaypoint.syncRotations = bool(body, "sync_rotations", bool(body, "syncRotations", true));
  groupWaypoint.minX = num(body, "min_x", num(body, "minX", 0));
  groupWaypoint.maxX = num(body, "max_x", num(body, "maxX", 0));
  groupWaypoint.minY = num(body, "min_y", num(body, "minY", 0));
  groupWaypoint.maxY = num(body, "max_y", num(body, "maxY", 0));
  groupWaypoint.lifts = Array.isArray(body.lifts)
    ? body.lifts.map((lift) => toWaypointT(lift))
    : [];
  groupWaypoint.banks = Array.isArray(body.banks)
    ? body.banks.map((bank) => toBankWaypointT(bank))
    : [];
  return groupWaypoint;
};

const toBankWaypointT = (raw: unknown): BankWaypointT => {
  const body = (raw ?? {}) as Record<string, unknown>;
  const bankWaypoint = new BankWaypointT();
  bankWaypoint.id = uuidField(body, "id");
  bankWaypoint.nickname = nullable(body, "nickname");
  bankWaypoint.bankName = nullable(body, "bank_name") ?? nullable(body, "bankName");
  bankWaypoint.x = num(body, "x", 0);
  bankWaypoint.y = num(body, "y", 0);
  bankWaypoint.backgroundColorHex =
    nullable(body, "background_color_hex") ?? nullable(body, "backgroundColorHex");
  bankWaypoint.backgroundOpacity = num(
    body,
    "background_opacity",
    num(body, "backgroundOpacity", 0),
  );
  bankWaypoint.backgroundWidth = num(body, "background_width", num(body, "backgroundWidth", 0));
  bankWaypoint.backgroundHeight = num(body, "background_height", num(body, "backgroundHeight", 0));
  bankWaypoint.displayName = bool(body, "display_name", bool(body, "displayName", false));
  bankWaypoint.fontSize = num(body, "font_size", num(body, "fontSize", 0));
  bankWaypoint.fontColorHex = nullable(body, "font_color_hex") ?? nullable(body, "fontColorHex");
  bankWaypoint.backgroundRotationDeg = num(
    body,
    "background_rotation_deg",
    num(body, "backgroundRotationDeg", 0),
  );
  bankWaypoint.nameRotationDeg = num(body, "name_rotation_deg", num(body, "nameRotationDeg", 0));
  bankWaypoint.syncRotations = bool(body, "sync_rotations", bool(body, "syncRotations", true));
  bankWaypoint.minX = num(body, "min_x", num(body, "minX", 0));
  bankWaypoint.maxX = num(body, "max_x", num(body, "maxX", 0));
  bankWaypoint.minY = num(body, "min_y", num(body, "minY", 0));
  bankWaypoint.maxY = num(body, "max_y", num(body, "maxY", 0));
  bankWaypoint.linkLifts = bool(body, "link_lifts", bool(body, "linkLifts", true));
  bankWaypoint.lifts = Array.isArray(body.lifts) ? body.lifts.map((lift) => toWaypointT(lift)) : [];
  return bankWaypoint;
};

function buildWaypointMutationT(body: Record<string, unknown>): WaypointT {
  const waypoint = toWaypointT(body);
  waypoint.id = uuidFieldOrZeroWhenAbsent(body, "id");
  return waypoint;
}

function buildGroupWaypointMutationT(body: Record<string, unknown>): GroupWaypointT {
  const groupWaypoint = toGroupWaypointT(body);
  groupWaypoint.id = uuidFieldOrZeroWhenAbsent(body, "id");
  return groupWaypoint;
}

function buildBankWaypointMutationT(body: Record<string, unknown>): BankWaypointT {
  const bankWaypoint = toBankWaypointT(body);
  bankWaypoint.id = uuidFieldOrZeroWhenAbsent(body, "id");
  bankWaypoint.lifts = Array.isArray(body.lifts)
    ? body.lifts.map((lift) => buildWaypointMutationT(lift as Record<string, unknown>))
    : [];
  return bankWaypoint;
}

// ---------------------------------------------------------------------------
// ContactInfo helper — builds ContactInfoT[] from a body.contact_info array
// ---------------------------------------------------------------------------

function buildContactInfoFromBody(body: Record<string, unknown>): ContactInfoT[] {
  const raw = body.contact_info;
  if (!Array.isArray(raw)) return [];
  // organization_id is required by the schema. Per-entry id is preserved so the
  // update handler can target existing rows; an empty id resolves to a zero
  // UUID, which the server treats as a new entry. user_id is resolved
  // server-side from the target user, so a zero-UUID placeholder is used.
  const orgId = requiredUuidField(body, "org_id");
  return raw.map((c: Record<string, unknown>) => {
    const t = new ContactInfoT();
    t.id = requiredUuidField(c, "id");
    t.userId = ZERO_UUID;
    t.organizationId = orgId;
    t.phone = str(c, "phone");
    t.phoneCode = str(c, "phone_code");
    t.address = str(c, "address");
    t.priority = num(c, "priority", 0);
    return t;
  });
}

// ---------------------------------------------------------------------------
// NotificationRecipient helper — builds NotificationRecipientT[] from a
// body.recipients array. Each recipient is a {type, value} contact destination
// owned by the notification. id/notification_id/organization_id are required by
// the schema; a per-row empty id resolves to the zero UUID (server treats it as
// a new row), and notification_id/organization_id are taken from the parent.
// ---------------------------------------------------------------------------

function buildNotificationRecipientsFromBody(
  body: Record<string, unknown>,
): NotificationRecipientT[] {
  const raw = body.recipients;
  if (!Array.isArray(raw)) return [];
  const notificationId = requiredUuidField(body, "id");
  const orgId = requiredUuidField(body, "organization_id");
  return raw.map((r: Record<string, unknown>) => {
    const t = new NotificationRecipientT();
    t.id = requiredUuidField(r, "id");
    t.notificationId = notificationId;
    t.organizationId = orgId;
    t.type = toNotificationType(str(r, "type"));
    t.value = str(r, "value");
    t.kind = toRecipientKind(str(r, "kind"));
    // A USER or ROLE entry names its target; an ADDRESS entry names neither,
    // and stringToUuidT of an empty string yields the zero UUID the schema's
    // optional fields read as absent.
    t.userId = stringToUuidT(str(r, "user_id"));
    t.roleId = stringToUuidT(str(r, "role_id"));
    t.channels = num(r, "channels");
    return t;
  });
}

// ---------------------------------------------------------------------------
// ChannelEndpoint helper — builds ChannelEndpointT[] from a body.endpoints array
// ---------------------------------------------------------------------------

function buildEndpointsFromBody(body: Record<string, unknown>): ChannelEndpointT[] {
  const raw = body.endpoints;
  if (!Array.isArray(raw)) return [];
  return raw.map((ep: Record<string, unknown>) => {
    const t = new ChannelEndpointT();
    t.id = requiredUuidField(ep, "id");
    t.channelId = uuidField(ep, "channelId");
    t.groupId = num(ep, "groupId", 0);
    t.priority = num(ep, "priority", 0);
    t.vendorSocketAddress = nullable(ep, "address");
    t.connectionType = intEnum(ep, "transportType") as ChannelType;
    t.baudRate = num(ep, "baudRate", 9600);
    t.adapterId = uuidField(ep, "adapterId");
    t.active = bool(ep, "active", true);
    return t;
  });
}

// ---------------------------------------------------------------------------
// Channel session helpers — Session.* tables from body.session and
// body.sessionCredentials
// ---------------------------------------------------------------------------

/**
 * A nested table of a body as a plain record, or null when the key holds none.
 *
 * A session table arrives in three shapes: the generated `*T` instance the
 * channel step builds, that instance after `structuredClone` (edge management
 * PUTs a clone of the loaded row, which keeps the fields and loses the
 * prototype), and plain JSON. Reading fields by name works for all three, which
 * is why the builders below construct each table afresh instead of passing the
 * body's object through: a clone has no `pack()` to call.
 */
function tableField(body: Record<string, unknown>, key: string): Record<string, unknown> | null {
  const v = body[key];
  return v !== null && typeof v === "object" ? (v as Record<string, unknown>) : null;
}

/**
 * Builds Session.SessionConfigT from `body.session`: protocol, username and the
 * telnet / ssh options. A body without one packs no table, which LiftCloud
 * reads as "leave the stored session unchanged", so a body that never mentions
 * sessions cannot erase one.
 */
function buildSessionConfig(body: Record<string, unknown>): SessionConfigT | null {
  const raw = tableField(body, "session");
  if (raw === null) return null;
  const session = new SessionConfigT();
  session.protocol = intEnum(raw, "protocol", SessionProtocol.None) as SessionProtocol;
  session.username = nullable(raw, "username");
  session.telnet = buildTelnetOptions(raw);
  session.ssh = buildSshOptions(raw);
  return session;
}

/** Builds Session.TelnetOptionsT from `session.telnet`; an empty field means the default. */
function buildTelnetOptions(session: Record<string, unknown>): TelnetOptionsT | null {
  const raw = tableField(session, "telnet");
  if (raw === null) return null;
  const telnet = new TelnetOptionsT();
  telnet.usernamePrompt = nullable(raw, "usernamePrompt");
  telnet.passwordPrompt = nullable(raw, "passwordPrompt");
  telnet.readyMarker = nullable(raw, "readyMarker");
  telnet.failureMarker = nullable(raw, "failureMarker");
  return telnet;
}

/** Builds Session.SshOptionsT from `session.ssh`. */
function buildSshOptions(session: Record<string, unknown>): SshOptionsT | null {
  const raw = tableField(session, "ssh");
  if (raw === null) return null;
  const ssh = new SshOptionsT();
  ssh.hostKeyFingerprint = nullable(raw, "hostKeyFingerprint");
  ssh.acceptAnyHostKey = bool(raw, "acceptAnyHostKey", false);
  ssh.command = nullable(raw, "command");
  ssh.requestPty = bool(raw, "requestPty", false);
  return ssh;
}

/**
 * A write-only secret as typed, or null when it is absent or empty. Never
 * trimmed: a space in a password is part of the password.
 */
function secretField(row: Record<string, unknown>, key: string): string | null {
  const v = nullable(row, key);
  return v === "" ? null : v;
}

/**
 * Builds Session.SessionCredentialsT from `body.sessionCredentials`. Each
 * secret is optional: an absent or empty one is left off the wire, which
 * LiftCloud reads as "keep the stored secret", and a body carrying no secret at
 * all packs no table.
 */
function buildSessionCredentials(body: Record<string, unknown>): SessionCredentialsT | null {
  const raw = tableField(body, "sessionCredentials");
  if (raw === null) return null;
  const credentials = new SessionCredentialsT();
  credentials.password = secretField(raw, "password");
  credentials.privateKey = secretField(raw, "privateKey");
  credentials.privateKeyPassphrase = secretField(raw, "privateKeyPassphrase");
  const secrets = [credentials.password, credentials.privateKey, credentials.privateKeyPassphrase];
  return secrets.some((secret) => secret !== null) ? credentials : null;
}

// ---------------------------------------------------------------------------
// Schema hash cache — populated by initializeRequestSerializers()
// ---------------------------------------------------------------------------

const schemaHashCache = new Map<string, string>();

async function getOrComputeHash(typeName: string): Promise<string> {
  const cached = schemaHashCache.get(typeName);
  if (cached) return cached;
  const encoder = new TextEncoder();
  const hex = await sha256Hex(encoder.encode(typeName));
  schemaHashCache.set(typeName, hex);
  return hex;
}

// ---------------------------------------------------------------------------
// Adapter buildT helper
// ---------------------------------------------------------------------------

/**
 * Builds an AdapterT from a request body.
 *
 * The frontend (AdapterEditor.tsx) sends snake_case field names matching the
 * JSON API model. FlatBuffer-decoded adapter data uses camelCase field names.
 * This helper reads both, preferring snake_case so that the common create/update
 * path works correctly. Enum fields (type, protocolType, liftType, authType)
 * arrive as strings ("WEBSOCKET", "MCE", "ELEVATOR", "PASSWORD") and are
 * converted to their FlatBuffer numeric values.
 */
/**
 * Read a floor base off a request body, tolerating both spellings and the
 * numeric strings an antd InputNumber can hand back.
 *
 * Returns 0 for anything unusable, which is the schema's own default and means
 * "no shift" — never a guess. int16 on the wire, so the value is clamped rather
 * than allowed to wrap silently.
 */
function floorBaseField(body: Record<string, unknown>, snake: string, camel: string): number {
  const raw = body[snake] ?? body[camel];
  const n = typeof raw === "string" ? Number(raw) : raw;
  if (typeof n !== "number" || !Number.isFinite(n)) return 0;

  return Math.max(-32768, Math.min(32767, Math.trunc(n)));
}

/**
 * The wire value that means "no cap" on `Archive.Adapter.max_cars`.
 *
 * A FlatBuffer scalar cannot carry null, so the schema defines 0 as the
 * sentinel for a NULL column. 0 is safe precisely because a cap of zero cars
 * is meaningless -- a cap of ONE is the smallest real one, and stays
 * expressible.
 */
export const MAX_CARS_WIRE_NO_CAP = 0;

/**
 * Read a nullable int16 off a request body.
 *
 * Returns null for absent, empty, or unparseable input, which for max_cars is
 * the SAFE default: null means "no cap". This is deliberately NOT floorBaseField
 * above — that one returns 0 for the same inputs, because 0 is a legitimate
 * "no shift" there. Here 0 would mean a cap of zero cars, so the two cannot
 * share an implementation.
 */
function nullableInt16Field(
  body: Record<string, unknown>,
  snake: string,
  camel: string,
): number | null {
  const raw = body[snake] ?? body[camel];
  if (raw === null || raw === undefined || raw === "") return null;

  const n = typeof raw === "string" ? Number(raw) : raw;
  if (typeof n !== "number" || !Number.isFinite(n)) return null;

  return Math.max(-32768, Math.min(32767, Math.trunc(n)));
}

function buildAdapterT(body: Record<string, unknown>, isCreate: boolean): AdapterT {
  const t = new AdapterT();

  // ID: zero UUID for creates (server assigns), explicit UUID for updates.
  t.id = isCreate ? requiredUuidField(body, "id") : (uuidField(body, "id") ?? null);

  // UUID: snake_case (AdapterEditor) with camelCase fallback (FlatBuffer-decoded).
  t.leadingZeroesId = uuidField(body, "leading_zeroes_id") ?? uuidField(body, "leadingZeroesId");

  t.name = str(body, "name");
  t.version = str(body, "version");
  t.active = bool(body, "active", true);

  // Enum fields: body may have string values ("WEBSOCKET", "MCE", "ELEVATOR", "PASSWORD")
  // or numeric FlatBuffer enum values when coming from a decoded adapter.
  const rawType = body.type;
  t.type =
    typeof rawType === "number"
      ? (rawType as AdapterType)
      : toAdapterType(typeof rawType === "string" ? rawType : undefined);

  const rawProto = dual(body, "protocol_type", "protocolType");
  t.protocolType =
    typeof rawProto === "number"
      ? (rawProto as AdapterProtocol)
      : toAdapterProtocol(typeof rawProto === "string" ? rawProto : undefined);

  const rawLift = dual(body, "lift_type", "liftType");
  t.liftType = toLiftType(
    typeof rawLift === "string" || typeof rawLift === "number" ? rawLift : undefined,
  );

  const rawAuth = dual(body, "auth_type", "authType");
  t.authType =
    typeof rawAuth === "number"
      ? (rawAuth as AdapterAuth)
      : toAdapterAuth(typeof rawAuth === "string" ? rawAuth : undefined);

  const rawVendorFloorIndexing = dual(body, "vendor_floor_indexing", "vendorFloorIndexing");
  t.vendorFloorIndexing = toVendorFloorIndexing(
    typeof rawVendorFloorIndexing === "string" || typeof rawVendorFloorIndexing === "number"
      ? rawVendorFloorIndexing
      : undefined,
  );

  // The two floor bases. AdapterEditor puts these in the JSON body, but every
  // POST/PUT to the adapter API is re-encoded to a FlatBuffer by
  // flatBufferBaseQuery before it leaves the page — so a key this serializer
  // does not copy is silently dropped in the browser and never reaches the
  // server at all. That is what made both inputs read back as 0 no matter what
  // an operator typed.
  t.positionFloorBase = floorBaseField(body, "position_floor_base", "positionFloorBase");
  t.callFloorBase = floorBaseField(body, "call_floor_base", "callFloorBase");

  // The expansion cap rides the same path, but needs one step the bases do not.
  //
  // `Archive.Adapter.max_cars` is `int16 = 0`, and a FlatBuffer scalar cannot
  // carry null: an absent slot and one written at its default are
  // indistinguishable on the wire. The column behind it IS nullable and NULL is
  // its "no cap" value, so the schema defines 0 as the sentinel for NULL --
  // safe because a cap of zero cars is meaningless, while a cap of ONE is
  // legitimate and must stay expressible. The server maps 0 back to NULL.
  //
  // Null is therefore encoded as 0 HERE. A key this serializer does not copy
  // never leaves the browser, so omitting it would make an operator's manual
  // override unable to reach the server at all.
  t.maxCars = nullableInt16Field(body, "max_cars", "maxCars") ?? MAX_CARS_WIRE_NO_CAP;
  // direction_aware_keys: the per-adapter opt-in for Key.direction. Absent
  // reads as opted OUT, matching the server's DirectionAwareKeysEnabled
  // reading of a nil column.
  t.directionAwareKeys = bool(body, "direction_aware_keys");

  // JSON blob fields: snake_case (AdapterEditor) with camelCase fallback.
  // jsonField handles objects/arrays by JSON.stringify-ing them.
  t.interfaceJson = jsonField(body, "interface") ?? jsonField(body, "interfaceJson");
  t.offsetMappingJson = jsonField(body, "offset_mapping") ?? jsonField(body, "offsetMappingJson");
  t.cdoInputJson = jsonField(body, "cdo_input") ?? jsonField(body, "cdoInputJson");
  t.cdoTemplateJson = jsonField(body, "cdo_template") ?? jsonField(body, "cdoTemplateJson");
  t.bacnetConfigJson = jsonField(body, "bacnet_config") ?? jsonField(body, "bacnetConfig");
  t.bacnetBankMappingsJson =
    jsonField(body, "bacnet_bank_mappings") ?? jsonField(body, "bacnetBankMappings");
  t.dictionaryJson = jsonField(body, "dictionary") ?? jsonField(body, "dictionaryJson");
  t.socketMessagePriorityJson =
    jsonField(body, "socket_message_priority") ?? jsonField(body, "socketMessagePriorityJson");
  t.intraContentFlagsJson =
    jsonField(body, "intra_content_flags") ?? jsonField(body, "intraContentFlagsJson");

  // Binary CDO config (OPC UA and other binary-only protocols): AdapterEditor
  // sends the packed CDO as a base64 string in `cdo_binary` plus a
  // `cdo_schema_name` discriminator directly on the create/update body. Both
  // MUST be forwarded here — the AdapterT field is raw bytes (number[]), so the
  // base64 string is decoded first (inverse of opcUaMappingBytesToBase64).
  // Without these the FlatBuffer re-encode drops them, leaving the adapter with
  // no CDO server-side and the packer defaulting to "modbus_template" (see
  // AdapterEditor.buildAdapterBody and LiftCloud SchemaNameForProtocol).
  const cdoBinary = base64BytesField(body, "cdo_binary", "cdoBinary");
  if (cdoBinary) t.cdoBinary = cdoBinary;
  t.cdoSchemaName = nullable(body, "cdo_schema_name") ?? nullable(body, "cdoSchemaName");

  // Pack auth credentials as FlatBuffer PasswordContent into authBinary
  const creds = body.auth_credentials as Record<string, string> | undefined;
  if (creds && (creds.username || creds.password)) {
    const pc = new PasswordContentT(creds.username ?? "", creds.password ?? "");
    const authBuilder = new flatbuffers.Builder(128);
    const offset = pc.pack(authBuilder);
    authBuilder.finish(offset);
    t.authBinary = Array.from(authBuilder.asUint8Array());
  }

  return t;
}

// ---------------------------------------------------------------------------
// Key buildT helper
// ---------------------------------------------------------------------------

/**
 * Populates a KeyT from a request body.
 *
 * Shared between CreateKey (POST) and UpdateKey (PUT). The only difference is
 * how the id is resolved, which the caller supplies. Every field the backend
 * persists must be forwarded here — missing a field causes the FlatBuffer
 * packing to emit the schema default (usually 0 or ""), which then clobbers
 * the stored row via the FB path (bypassing Gin's JSON-binding validation).
 *
 * `content_type` and the `lower_*` pair fields are mandatory for correct
 * bit/byte categorization and paired-bit reconstruction. `value` is the
 * JSON-stringified value payload and is stored as `value_binary` on the key.
 */
function buildKeyT(body: Record<string, unknown>, id: UuidT | null): KeyT {
  const valueStr = str(body, "value");
  const valueBinary = valueStr !== "" ? Array.from(new TextEncoder().encode(valueStr)) : [];

  // Assignment style (not KeyT's old positional constructor): every generated *T
  // field is a promoted constructor parameter with its own default, so a
  // positional call silently absorbs a dropped trailing argument with zero
  // compile error — that is exactly how `groupShared` (schema field #24) went
  // unassigned here. createdAt/updatedAt/deletedAt are intentionally omitted;
  // KeyT's own constructor defaults them to null, identical to the previous
  // explicit `undefined` arguments.
  const t = new KeyT();
  t.id = id;
  t.adapterId = stringToUuidT(str(body, "adapter_id"));
  t.fbsDeviceType = toLiftType(str(body, "fbs_device_type"));
  t.encodedType = num(body, "encoded_type");
  t.messageContent = num(body, "message_content");
  t.messageValue = num(body, "message_value");
  t.contentType = num(body, "content_type");
  t.position = num(body, "position");
  t.lowerEncodedType = num(body, "lower_encoded_type");
  t.lowerMessageContent = num(body, "lower_message_content");
  t.lowerMessageValue = num(body, "lower_message_value");
  t.lowerFbsDeviceType = toLiftType(str(body, "lower_fbs_device_type"));
  t.lowerPosition = num(body, "lower_position");
  t.floorsBased = bool(body, "floors_based");
  t.unitsBased = bool(body, "units_based");
  t.bankLevel = bool(body, "bank_level");
  t.debounceTime = num(body, "debounce_time");
  t.floorIndexing = nullable(body, "floor_indexing");
  t.valueBinary = valueBinary;
  t.individual = bool(body, "individual");
  // group_shared: operator-facing "Group Shared" toggle in the Key mapping
  // editor (AdapterEditor.tsx serializeApiKey, CdoLn1MappingEditor.tsx). Was
  // never read here, so it silently saved as false regardless of what was
  // checked in the wizard.
  t.groupShared = bool(body, "group_shared");
  // direction: which of the two LN1 maps this key feeds (Archive.KeyDirection).
  // Absent or unparseable reads as BOTH, matching the column, the wire and the
  // server's DirectionOrDefault — a key that predates the field must keep
  // feeding both maps.
  t.direction = num(body, "direction") ?? KeyDirection.BOTH;
  return t;
}

// ---------------------------------------------------------------------------
// TimedAction buildT helper
// ---------------------------------------------------------------------------

/**
 * Populates a TimedActionT from a request body (the snake_case TimedActionDraft
 * the scheduled-action editor submits). Shared by CreateTimedAction (POST) and
 * UpdateTimedAction (PUT) so the field mapping cannot diverge.
 *
 * The backend's UnpackTimedActionRequest ignores the body id (POST: server
 * assigns; PUT: id comes from the URL path), but `id`, `bank_id`,
 * `organization_id` and `name` are required by TimedActionT.pack(), so a create
 * sends a zero-UUID placeholder. start_at / stop_at are ISO-8601 strings parsed
 * server-side as RFC3339; an absent value stays null so a PUT leaves the stored
 * timestamp intact. Fields are read by their snake_case names because the only
 * caller is the timed-actions RTK mutation, whose body is always the draft.
 */
function buildTimedActionT(body: Record<string, unknown>, isCreate: boolean): TimedActionT {
  const t = new TimedActionT();
  t.id = isCreate ? ZERO_UUID : uuidField(body, "id");
  t.bankId = uuidFieldOrZeroWhenAbsent(body, "bank_id");
  t.organizationId = uuidFieldOrZeroWhenAbsent(body, "organization_id");
  t.liftId = uuidField(body, "lift_id");
  t.name = str(body, "name");
  t.enabled = bool(body, "enabled", true);
  t.startMessageValue = num(body, "start_message_value", 0);
  t.stopMessageValue = num(body, "stop_message_value", 0);
  // Independent active state for the start/stop command (UiCmd.active at
  // dispatch). Defaults to true, matching TimedActionT's own constructor
  // default and emptyTimedActionDraft — an omitted body (there should not be
  // one, since the form always sets these) still dispatches active.
  t.startMessageActive = bool(body, "start_message_active", true);
  t.stopMessageActive = bool(body, "stop_message_active", true);
  t.position = num(body, "position", 0);
  t.validStates = strArray(body, "valid_states");
  t.recurrenceMode = num(body, "recurrence_mode", RecurrenceMode.ONE_SHOT) as RecurrenceMode;
  t.startSecondsOfDay = num(body, "start_seconds_of_day", 0);
  t.stopSecondsOfDay = num(body, "stop_seconds_of_day", 0);
  t.daysOfWeek = num(body, "days_of_week", 0);
  t.timezone = str(body, "timezone");
  t.stopNextDay = bool(body, "stop_next_day");
  t.startAt = nullable(body, "start_at");
  t.stopAt = nullable(body, "stop_at");
  t.weekInterval = num(body, "week_interval", 0);
  t.daysOfMonth = num(body, "days_of_month", 0);
  t.intervalSeconds = num(body, "interval_seconds", 0);
  // MONTHLY_WEEKDAY. Default ordinal 1 matches TimedActionDraft's own
  // default — 0 is never a valid ordinal, so there is no safe "unused" value
  // to fall back to for a mode that isn't MONTHLY_WEEKDAY either.
  t.monthWeekOrdinal = num(body, "month_week_ordinal", 1);
  t.monthWeekLast = bool(body, "month_week_last", false);
  // Granular targeting. Each is empty by default, and the server reads an empty
  // value as "fall back to the scalar bank_id / lift_id / position", so an
  // action that does not use granular targeting is serialized exactly as before.
  t.bankIds = uuidArrayField(body, "bank_ids");
  t.liftIds = uuidArrayField(body, "lift_ids");
  t.floorSpec = str(body, "floor_spec");
  return t;
}

/**
 * Reads a string[] of UUIDs into the FlatBuffer Uuid vector the Object API
 * expects. Entries that do not convert are dropped rather than becoming the nil
 * UUID, which would name a bank or lift that cannot exist.
 */
function uuidArrayField(body: Record<string, unknown>, key: string): UuidT[] {
  const raw = body[key];
  if (!Array.isArray(raw)) return [];

  return raw
    .map((id) => (typeof id === "string" ? stringToUuidT(id) : null))
    .filter((id): id is UuidT => id !== null);
}

// ---------------------------------------------------------------------------
// Serializer registration queue
// ---------------------------------------------------------------------------

interface SerializerSpec {
  name: SerializerName;
  typeName: string;
  buildT: (body: Record<string, unknown>) => {
    pack(builder: flatbuffers.Builder): flatbuffers.Offset;
  };
}

// ---------------------------------------------------------------------------
// Scope permission vector builders for Role serialization
// ---------------------------------------------------------------------------

/**
 * Convert a scope number to a PermissionAction enum value.
 * Validates against known values; falls back to VIEW for unknown values.
 */
function toPermissionActionEnum(raw: unknown): PermissionAction {
  const n = typeof raw === "number" ? raw : Number(raw ?? 0);
  if (n === PermissionAction.CREATE) return PermissionAction.CREATE;
  if (n === PermissionAction.UPDATE) return PermissionAction.UPDATE;
  if (n === PermissionAction.DELETE) return PermissionAction.DELETE;
  if (n === PermissionAction.ACTION) return PermissionAction.ACTION;
  return PermissionAction.VIEW;
}

/** Build RoleBankPermissionT[] from the body's bank_permissions array. */
function buildBankPermissions(body: Record<string, unknown>): RoleBankPermissionT[] {
  const raw = body.bank_permissions;
  if (!Array.isArray(raw)) return [];
  return raw.map((item: unknown) => {
    const g = (item ?? {}) as Record<string, unknown>;
    const t = new RoleBankPermissionT();
    t.id = ZERO_UUID;
    t.roleId = ZERO_UUID;
    t.bankId = stringToUuidT(String(g.entity_id ?? "")) ?? ZERO_UUID;
    t.action = toPermissionActionEnum(g.action);
    return t;
  });
}

/**
 * Build the Archive.Role `permission_grants` vector from the role's explicit
 * decisions: a top-level `permission_grants` object when the body has one,
 * otherwise the one roleMenuSaveJson embeds at the root of `menu`. The menu
 * keeps its embedded copy (menuJson above), which is what a LiftCloud older
 * than the vector reads; one that knows the vector reads it first. No
 * decisions gives an empty vector, which tells LiftCloud to read the menu.
 */
function buildRolePermissionGrants(body: Record<string, unknown>): RolePermissionGrantT[] {
  const menu = body.menu;
  const embedded =
    menu && typeof menu === "object" ? (menu as Record<string, unknown>).permission_grants : null;
  return permissionGrantsToVector(grantsObject(body.permission_grants ?? embedded));
}

/** Build RoleLiftPermissionT[] from the body's lift_permissions array. */
function buildLiftPermissions(body: Record<string, unknown>): RoleLiftPermissionT[] {
  const raw = body.lift_permissions;
  if (!Array.isArray(raw)) return [];
  return raw.map((item: unknown) => {
    const g = (item ?? {}) as Record<string, unknown>;
    const t = new RoleLiftPermissionT();
    t.id = ZERO_UUID;
    t.roleId = ZERO_UUID;
    t.liftId = stringToUuidT(String(g.entity_id ?? "")) ?? ZERO_UUID;
    t.action = toPermissionActionEnum(g.action);
    return t;
  });
}

/** Build RoleFloorPermissionT[] from the body's floor_permissions array. */
function buildFloorPermissions(body: Record<string, unknown>): RoleFloorPermissionT[] {
  const raw = body.floor_permissions;
  if (!Array.isArray(raw)) return [];
  return raw.map((item: unknown) => {
    const g = (item ?? {}) as Record<string, unknown>;
    const t = new RoleFloorPermissionT();
    t.id = ZERO_UUID;
    t.roleId = ZERO_UUID;
    t.bankFloorId = stringToUuidT(String(g.entity_id ?? "")) ?? ZERO_UUID;
    t.action = toPermissionActionEnum(g.action);
    return t;
  });
}

const serializerQueue: SerializerSpec[] = [
  // ---- Organization --------------------------------------------------------
  {
    name: "CreateOrganization",
    typeName: "Archive.Organization",
    buildT: (body) => {
      const t = new OrganizationT();
      t.id = requiredUuidField(body, "id");
      t.name = str(body, "name");
      t.address = str(body, "address");
      t.city = str(body, "city");
      t.country = str(body, "country");
      t.phoneCode = str(body, "phone_code");
      t.phone = str(body, "phone");
      t.state = str(body, "state");
      t.parentId = stringToUuidT(str(body, "parent_id"));
      t.type = toOrgType(str(body, "type"));
      t.status = toOrgStatus(str(body, "status"));
      t.doorUnknownAsOpening = bool(body, "door_unknown_as_opening", true);
      t.hideUnconfiguredDoors = bool(body, "hide_unconfigured_doors", true);
      t.actionFiringMode = resolveOrganizationActionFiringMode(body);
      return t;
    },
  },
  {
    name: "UpdateOrganization",
    typeName: "Archive.Organization",
    buildT: (body) => {
      const t = new OrganizationT();
      t.id = stringToUuidT(str(body, "id"));
      t.name = str(body, "name");
      t.address = str(body, "address");
      t.city = str(body, "city");
      t.country = str(body, "country");
      t.phoneCode = str(body, "phone_code");
      t.phone = str(body, "phone");
      t.state = str(body, "state");
      t.parentId = stringToUuidT(str(body, "parent_id"));
      t.type = toOrgType(str(body, "type"));
      t.status = toOrgStatus(str(body, "status"));
      t.doorUnknownAsOpening = bool(body, "door_unknown_as_opening", true);
      t.hideUnconfiguredDoors = bool(body, "hide_unconfigured_doors", true);
      t.actionFiringMode = resolveOrganizationActionFiringMode(body);
      return t;
    },
  },

  // ---- Server --------------------------------------------------------------
  {
    name: "CreateServer",
    typeName: "Archive.Server",
    buildT: (body) => {
      const t = new ServerT();
      t.id = requiredUuidField(body, "id");
      t.orgId = stringToUuidT(str(body, "org_id"));
      t.name = str(body, "name");
      t.ip = str(body, "ip");
      t.active = bool(body, "active", true);
      t.fbEnabled = bool(body, "fb_enabled");
      t.macAddress = nullable(body, "mac_address");
      t.serialNumber = nullable(body, "serial_number");
      t.edgeHostname = nullable(body, "edge_hostname");
      t.siteNetwork = siteNetworkField(body);
      t.dbPass = nullable(body, "db_pass");
      t.tunnelPort = num(body, "tunnel_port");
      t.pgPort = num(body, "pg_port");
      t.memoryGb = num(body, "memory_gb");
      t.diskGb = num(body, "disk_gb");
      t.syncSched = str(body, "sync_sched");
      t.syncQuestDbSched = str(body, "sync_quest_db_sched");
      t.recordSyncSize = num(body, "record_sync_size", 100);
      t.localPgPort = num(body, "local_pg_port", 6432);
      return t;
    },
  },
  {
    name: "UpdateServer",
    typeName: "Archive.Server",
    buildT: (body) => {
      const t = new ServerT();
      t.id = stringToUuidT(str(body, "id"));
      t.orgId = stringToUuidT(str(body, "org_id"));
      t.name = str(body, "name");
      t.ip = str(body, "ip");
      t.active = bool(body, "active", true);
      t.fbEnabled = bool(body, "fb_enabled");
      t.macAddress = nullable(body, "mac_address");
      t.serialNumber = nullable(body, "serial_number");
      t.edgeHostname = nullable(body, "edge_hostname");
      t.siteNetwork = siteNetworkField(body);
      t.dbPass = nullable(body, "db_pass");
      t.tunnelPort = num(body, "tunnel_port");
      t.pgPort = num(body, "pg_port");
      t.memoryGb = num(body, "memory_gb");
      t.diskGb = num(body, "disk_gb");
      t.syncSched = str(body, "sync_sched");
      t.syncQuestDbSched = str(body, "sync_quest_db_sched");
      t.recordSyncSize = num(body, "record_sync_size", 100);
      t.localPgPort = num(body, "local_pg_port", 6432);
      return t;
    },
  },

  // ---- Adapter -------------------------------------------------------------
  {
    name: "CreateAdapter",
    typeName: "Archive.Adapter",
    buildT: (body) => buildAdapterT(body, true),
  },
  {
    name: "UpdateAdapter",
    typeName: "Archive.Adapter",
    buildT: (body) => buildAdapterT(body, false),
  },

  // ---- Channel -------------------------------------------------------------
  {
    name: "CreateChannel",
    typeName: "Archive.Channel",
    buildT: (body) => {
      const t = new ChannelT();
      t.id = requiredUuidField(body, "id");
      t.serverId = uuidField(body, "serverId");
      t.organizationId = uuidField(body, "organizationId");
      t.leadingZeroesId = uuidField(body, "leadingZeroesId");
      t.name = str(body, "name");
      t.type = intEnum(body, "type") as ChannelType;
      t.connectionType = intEnum(body, "connectionType") as ChannelType;
      // Write-direction fallback for merged channels (see ControlWriteMode's own
      // doc comment). StepChannels.tsx sends this and reads it back on hydrate —
      // it was never forwarded here, so it silently saved as ACTIVE_ENDPOINT (0)
      // regardless of what was chosen.
      t.controlWriteMode = intEnum(body, "controlWriteMode") as ControlWriteMode;
      t.index = str(body, "index");
      t.active = bool(body, "active", true);
      t.routable = bool(body, "routable", false);
      t.baudRate = num(body, "baudRate", 9600);
      t.heartbeatFrequency = num(body, "heartbeatFrequency");
      t.heartbeatMessage = (body.heartbeatMessage as MessageT | null | undefined) ?? null;
      t.vendorSocketAddress = nullable(body, "vendorSocketAddress");
      t.routableSocketAddress = nullable(body, "routableSocketAddress");
      t.reportingSocketAddress = nullable(body, "reportingSocketAddress");
      t.opcUaEndpointUrl = dualNullable(body, "opc_ua_endpoint_url", "opcUaEndpointUrl");
      t.heartbeatMessage = (body.heartbeatMessage as MessageT | null | undefined) ?? null;
      t.mode = toChannelMode(intEnum(body, "mode"));
      t.mergePolicy = toMergePolicy(intEnum(body, "mergePolicy"));
      t.endpoints = buildEndpointsFromBody(body);
      t.mtlsEnabled = bool(body, "mtlsEnabled", false);
      t.mtlsSecretName = nullable(body, "mtlsSecretName") ?? "";
      // Session protocol (telnet, SSH) and its write-only secrets. A body
      // without `session` packs no table, so the channel is created with
      // protocol None, and only a typed secret is packed. The clear flags are
      // read here too, so create and update read the same keys.
      t.session = buildSessionConfig(body);
      t.sessionCredentials = buildSessionCredentials(body);
      t.sessionPasswordClear = bool(body, "sessionPasswordClear", false);
      t.sessionPrivateKeyClear = bool(body, "sessionPrivateKeyClear", false);
      return t;
    },
  },
  {
    name: "UpdateChannel",
    typeName: "Archive.Channel",
    buildT: (body) => {
      const t = new ChannelT();
      t.id = uuidField(body, "id");
      t.serverId = uuidField(body, "serverId");
      t.organizationId = uuidField(body, "organizationId");
      t.leadingZeroesId = uuidField(body, "leadingZeroesId");
      t.name = str(body, "name");
      t.type = intEnum(body, "type") as ChannelType;
      t.connectionType = intEnum(body, "connectionType") as ChannelType;
      // Write-direction fallback for merged channels (see ControlWriteMode's own
      // doc comment). StepChannels.tsx sends this and reads it back on hydrate —
      // it was never forwarded here, so it silently saved as ACTIVE_ENDPOINT (0)
      // regardless of what was chosen.
      t.controlWriteMode = intEnum(body, "controlWriteMode") as ControlWriteMode;
      t.index = str(body, "index");
      t.active = bool(body, "active", true);
      t.routable = bool(body, "routable", false);
      t.baudRate = num(body, "baudRate", 9600);
      t.heartbeatFrequency = num(body, "heartbeatFrequency");
      t.heartbeatMessage = (body.heartbeatMessage as MessageT | null | undefined) ?? null;
      t.vendorSocketAddress = nullable(body, "vendorSocketAddress");
      t.routableSocketAddress = nullable(body, "routableSocketAddress");
      t.reportingSocketAddress = nullable(body, "reportingSocketAddress");
      t.opcUaEndpointUrl = dualNullable(body, "opc_ua_endpoint_url", "opcUaEndpointUrl");
      t.heartbeatMessage = (body.heartbeatMessage as MessageT | null | undefined) ?? null;
      t.mode = toChannelMode(intEnum(body, "mode"));
      t.mergePolicy = toMergePolicy(intEnum(body, "mergePolicy"));
      t.endpoints = buildEndpointsFromBody(body);
      t.mtlsEnabled = bool(body, "mtlsEnabled", false);
      t.mtlsSecretName = nullable(body, "mtlsSecretName") ?? "";
      // Session protocol (telnet, SSH) and its write-only secrets. This PUT
      // carries the whole record, but a body without `session` packs no table
      // and LiftCloud keeps the stored session; an absent or empty secret keeps
      // the stored secret. Only the two clear flags erase one, so edge
      // management's clone of a loaded row changes nothing it did not edit.
      t.session = buildSessionConfig(body);
      t.sessionCredentials = buildSessionCredentials(body);
      t.sessionPasswordClear = bool(body, "sessionPasswordClear", false);
      t.sessionPrivateKeyClear = bool(body, "sessionPrivateKeyClear", false);
      return t;
    },
  },
  // True partial update — PATCH /channels/:channelID/active flips only the
  // `active` column server-side (see SetChannelActive in LiftCloud), unlike
  // the Channel POST/PUT serializers above which always send the full record.
  {
    name: "SetChannelActive",
    typeName: "Archive.ChannelActiveRequest",
    buildT: (body) => {
      const t = new ChannelActiveRequestT();
      t.active = bool(body, "active", false);
      return t;
    },
  },

  // ---- ChannelEndpoint -----------------------------------------------------
  {
    name: "CreateChannelEndpoint",
    typeName: "Archive.ChannelEndpoint",
    buildT: (body) => {
      const t = new ChannelEndpointT();
      t.id = requiredUuidField(body, "id");
      t.channelId = uuidField(body, "channelId");
      t.groupId = num(body, "groupId", 0);
      t.priority = num(body, "priority", 0);
      t.vendorSocketAddress = nullable(body, "vendorSocketAddress");
      t.routableSocketAddress = nullable(body, "routableSocketAddress");
      t.reportingSocketAddress = nullable(body, "reportingSocketAddress");
      t.serialPath = nullable(body, "serialPath");
      t.connectionType = intEnum(body, "connectionType") as ChannelType;
      t.baudRate = num(body, "baudRate", 9600);
      t.adapterId = uuidField(body, "adapterId");
      t.name = str(body, "name");
      t.active = bool(body, "active", true);
      return t;
    },
  },
  {
    name: "UpdateChannelEndpoint",
    typeName: "Archive.ChannelEndpoint",
    buildT: (body) => {
      const t = new ChannelEndpointT();
      t.id = uuidField(body, "id");
      t.channelId = uuidField(body, "channelId");
      t.groupId = num(body, "groupId", 0);
      t.priority = num(body, "priority", 0);
      t.vendorSocketAddress = nullable(body, "vendorSocketAddress");
      t.routableSocketAddress = nullable(body, "routableSocketAddress");
      t.reportingSocketAddress = nullable(body, "reportingSocketAddress");
      t.serialPath = nullable(body, "serialPath");
      t.connectionType = intEnum(body, "connectionType") as ChannelType;
      t.baudRate = num(body, "baudRate", 9600);
      t.adapterId = uuidField(body, "adapterId");
      t.name = str(body, "name");
      t.active = bool(body, "active", true);
      return t;
    },
  },

  // ---- Bank ----------------------------------------------------------------
  {
    name: "CreateBank",
    typeName: "Archive.Bank",
    buildT: (body) => {
      const t = new BankT();
      t.id = requiredUuidField(body, "id");
      t.channelId = uuidField(body, "channel_id") ?? uuidField(body, "channelId");
      t.adapterId = uuidField(body, "adapter_id") ?? uuidField(body, "adapterId");
      t.orgId =
        uuidField(body, "org_id") ??
        uuidField(body, "orgId") ??
        uuidField(body, "organizationId") ??
        ZERO_UUID;
      t.name = str(body, "name");
      t.type = toLiftType((body.type as string | number | undefined) ?? str(body, "type"));
      t.active = bool(body, "active", true);
      // rearDoors/numberOfLifts/floorCount/floorBelowLobby/startFloorNo/logicalPort
      // use the nested-default form (single-key lookup, then a fallback
      // single-key lookup, then the schema default) rather than a `??` chain
      // like channelId/adapterId/orgId above: num()/bool() never return
      // null/undefined, so `??` never falls through and a camelCase-only
      // caller would silently keep landing on the schema default. This is
      // the same idiom buildBankFloorsFromBody already uses. Every
      // BankInterface-spreading caller (BanksCreateEditForm.tsx,
      // KeysAssignInterfaceBank.tsx, BanksFloorNames.tsx, and others) sends
      // ONLY the camelCase spelling, so the single-key form was silently
      // zeroing/defaulting these fields on every save from those pages. See
      // project memory project_bank_logicalport_eventreportingaddress_clobber.
      //
      // Trade-off, deliberately accepted: requestSerializerFieldCoverage.test.ts's
      // default-parity check only evaluates a bool() call whose third argument
      // is a literal true/false — a nested bool() call (this line, and
      // buildBankFloorsFromBody's three pre-existing ones) is skipped rather
      // than mis-flagged (see that test's own comment at
      // requestSerializerFieldCoverage.test.ts:508-514). rearDoors was
      // previously checked (`bool(body, "rear_doors")`, a 2-arg call); this
      // fix silently drops it out of that check, the same cost frontDoors is
      // deliberately spared below. Accepted because the alternative — leaving
      // a real, confirmed camelCase-drop bug unfixed to keep one automated
      // guard's coverage — is worse. Extending the parity checker to unwrap a
      // nested bool()/num() call to its innermost literal default would
      // restore this (and buildBankFloorsFromBody's three) rather than lose
      // it; flagged as a follow-up, not attempted in this change.
      t.rearDoors = bool(body, "rear_doors", bool(body, "rearDoors", false));
      // frontDoors/visible pass an explicit `true` default because that is
      // BankT's own schema default (bank.ts:955-956, table defaults +true),
      // while bool()'s own default is `false`. This is NOT an inconsistency
      // to "fix" — rearDoors' schema default really is `false`
      // (bank.ts:401,930), so its explicit-`false` nested form above matches.
      //
      // frontDoors itself is deliberately NOT given the same camelCase
      // fallback as rearDoors above: BankInterface (src/interfaces/Bank.interface.ts)
      // has no frontDoors field at all, so no caller can currently supply it
      // camelCase — adding the fallback would drop this line out of the same
      // default-parity check for no behavioral benefit, unlike rearDoors
      // above where a real bug justified the trade-off.
      t.frontDoors = bool(body, "front_doors", true);
      t.visible = bool(body, "visible", true);
      t.numberOfLifts = num(body, "number_of_lifts", num(body, "numberOfLifts", 0));
      t.floorCount = num(body, "floor_count", num(body, "floorCount", 0));
      t.floorBelowLobby = num(body, "floor_below_lobby", num(body, "floorBelowLobby", 0));
      t.startFloorNo = num(body, "start_floor_no", num(body, "startFloorNo", 1));
      t.stops = num(body, "stops");
      t.columns = num(body, "columns");
      // Nested-default read like the counts above, plus the int16 wire-range guard.
      t.logicalPort = bankLogicalPort(body);
      // FlatBuffers can't express a null string, so BOTH an absent key AND an
      // explicit "" must map to null (not ""), matching BankT's own default
      // and letting UpdateBank's clear-on-omit behavior work correctly once
      // this reaches LiftCloud. nullable() alone isn't enough here: it only
      // returns null for a missing/null key, not for a present empty string,
      // and `??` doesn't fall through on "" (only on null/undefined) — so a
      // caller that sends `event_reporting_address: ""` to mean "clear it"
      // would otherwise put a present-but-blank value on the wire instead of
      // absent. Today's only caller (StepBanks.tsx) always omits rather than
      // empty-strings the key, but the shared serializer shouldn't rely on
      // every future caller replicating that discipline.
      const eventReportingAddress =
        nullable(body, "event_reporting_address") ?? nullable(body, "eventReportingAddress");
      t.eventReportingAddress = eventReportingAddress === "" ? null : eventReportingAddress;
      t.interfaceJson =
        jsonField(body, "interface_json") ??
        jsonField(body, "interfaceJson") ??
        jsonField(body, "interface");
      t.offsetMappingJson =
        jsonField(body, "offset_mapping_json") ??
        jsonField(body, "offsetMappingJson") ??
        jsonField(body, "offset_mapping") ??
        jsonField(body, "offsetMapping");
      t.socketMessagePriorityJson =
        jsonField(body, "socket_message_priority_json") ??
        jsonField(body, "socketMessagePriorityJson") ??
        jsonField(body, "socket_message_priority") ??
        jsonField(body, "socketMessagePriority");
      t.intraContentFlagsJson =
        jsonField(body, "intra_content_flags_json") ??
        jsonField(body, "intraContentFlagsJson") ??
        jsonField(body, "intra_content_flags") ??
        jsonField(body, "intraContentFlags");
      t.disabledColumnsJson =
        jsonField(body, "disabled_columns_json") ??
        jsonField(body, "disabledColumnsJson") ??
        jsonField(body, "disabled_columns") ??
        jsonField(body, "disabledColumns");
      // FlatBuffers-only (no JSON twin): the wizard sends the packed
      // OpcUaNodeLiftIndexBindings as a base64 string; BankT's field is raw
      // bytes (number[]), so decode it here — the inverse of
      // opcUaNodeLiftIndexBindingsToBase64. Without this the FlatBuffer
      // re-encode silently drops the binding on the Archive.Bank binary path.
      const opcUaNodeLiftMappingBinary = base64BytesField(
        body,
        "opc_ua_node_lift_mapping_binary",
        "opcUaNodeLiftMappingBinary",
      );
      if (opcUaNodeLiftMappingBinary) t.opcUaNodeLiftMappingBinary = opcUaNodeLiftMappingBinary;
      t.vendorLocationPath = vendorPath(body, "vendor_location_path", "vendorLocationPath");
      t.vendorSitePath = vendorPath(body, "vendor_site_path", "vendorSitePath");
      t.vendorGroupPath = vendorPath(body, "vendor_group_path", "vendorGroupPath");
      t.vendorHost = vendorPath(body, "vendor_host", "vendorHost");
      t.vendorPathsProvided = vendorPathsProvided(body);
      // Bank overlay — see overlayProvided(): only a save carrying the flag is
      // authoritative over the other two, so they are always packed as read.
      t.overlayBankId = overlayBankIdField(body);
      t.overlayLifts = overlayLiftsField(body);
      t.overlayProvided = overlayProvided(body);
      t.bankFloors = buildBankFloorsFromBody(body, t.id, t.orgId);
      return t;
    },
  },
  {
    name: "UpdateBank",
    typeName: "Archive.Bank",
    buildT: (body) => {
      const t = new BankT();
      t.id = requiredUuidField(body, "id");
      t.channelId = uuidField(body, "channel_id") ?? uuidField(body, "channelId");
      t.adapterId = uuidField(body, "adapter_id") ?? uuidField(body, "adapterId");
      t.orgId =
        uuidField(body, "org_id") ??
        uuidField(body, "orgId") ??
        uuidField(body, "organizationId") ??
        ZERO_UUID;
      t.name = str(body, "name");
      t.type = toLiftType((body.type as string | number | undefined) ?? str(body, "type"));
      t.active = bool(body, "active", true);
      // Nested-default form for the camelCase-only callers, and the
      // default-parity-check trade-off it costs rearDoors — see the full
      // explanation in the matching CreateBank comment. rearDoors specifically
      // has no effect through THIS entry today: models.BankUpdateRequest has
      // no RearDoors field at all (only models.BankRequest, the create
      // payload, does), so LiftCloud silently discards it on update
      // regardless of what this serializer sends. Fixed here anyway for
      // serializer-level correctness and consistency with CreateBank — see
      // project_bank_logicalport_eventreportingaddress_clobber for the full
      // per-field liveness table.
      t.rearDoors = bool(body, "rear_doors", bool(body, "rearDoors", false));
      // Explicit `true` defaults, and why rearDoors above is unchanged — see
      // the matching comment in CreateBank. frontDoors is deliberately left
      // on its single-key form too — no caller can supply it camelCase.
      t.frontDoors = bool(body, "front_doors", true);
      t.visible = bool(body, "visible", true);
      t.numberOfLifts = num(body, "number_of_lifts", num(body, "numberOfLifts", 0));
      t.floorCount = num(body, "floor_count", num(body, "floorCount", 0));
      t.floorBelowLobby = num(body, "floor_below_lobby", num(body, "floorBelowLobby", 0));
      t.startFloorNo = num(body, "start_floor_no", num(body, "startFloorNo", 1));
      t.stops = num(body, "stops");
      t.columns = num(body, "columns");
      // Nested-default read like the counts above, plus the int16 wire-range guard.
      t.logicalPort = bankLogicalPort(body);
      // Explicit null-vs-empty handling — see the matching comment in CreateBank.
      const eventReportingAddress =
        nullable(body, "event_reporting_address") ?? nullable(body, "eventReportingAddress");
      t.eventReportingAddress = eventReportingAddress === "" ? null : eventReportingAddress;
      t.interfaceJson =
        jsonField(body, "interface_json") ??
        jsonField(body, "interfaceJson") ??
        jsonField(body, "interface");
      t.offsetMappingJson =
        jsonField(body, "offset_mapping_json") ??
        jsonField(body, "offsetMappingJson") ??
        jsonField(body, "offset_mapping") ??
        jsonField(body, "offsetMapping");
      t.socketMessagePriorityJson =
        jsonField(body, "socket_message_priority_json") ??
        jsonField(body, "socketMessagePriorityJson") ??
        jsonField(body, "socket_message_priority") ??
        jsonField(body, "socketMessagePriority");
      t.intraContentFlagsJson =
        jsonField(body, "intra_content_flags_json") ??
        jsonField(body, "intraContentFlagsJson") ??
        jsonField(body, "intra_content_flags") ??
        jsonField(body, "intraContentFlags");
      t.disabledColumnsJson =
        jsonField(body, "disabled_columns_json") ??
        jsonField(body, "disabledColumnsJson") ??
        jsonField(body, "disabled_columns") ??
        jsonField(body, "disabledColumns");
      // FlatBuffers-only (no JSON twin) — see the matching comment in CreateBank.
      const opcUaNodeLiftMappingBinary = base64BytesField(
        body,
        "opc_ua_node_lift_mapping_binary",
        "opcUaNodeLiftMappingBinary",
      );
      if (opcUaNodeLiftMappingBinary) t.opcUaNodeLiftMappingBinary = opcUaNodeLiftMappingBinary;
      t.vendorLocationPath = vendorPath(body, "vendor_location_path", "vendorLocationPath");
      t.vendorSitePath = vendorPath(body, "vendor_site_path", "vendorSitePath");
      t.vendorGroupPath = vendorPath(body, "vendor_group_path", "vendorGroupPath");
      t.vendorHost = vendorPath(body, "vendor_host", "vendorHost");
      t.vendorPathsProvided = vendorPathsProvided(body);
      // Bank overlay — see overlayProvided(): only a save carrying the flag is
      // authoritative over the other two, so they are always packed as read.
      t.overlayBankId = overlayBankIdField(body);
      t.overlayLifts = overlayLiftsField(body);
      t.overlayProvided = overlayProvided(body);
      t.bankFloors = buildBankFloorsFromBody(body, t.id, t.orgId);
      return t;
    },
  },

  // ---- Lift ----------------------------------------------------------------
  {
    name: "CreateLift",
    typeName: "Archive.Lift",
    buildT: (body) => {
      const t = new LiftT();
      t.id = requiredUuidField(body, "id");
      t.bankId = uuidField(body, "bankId");
      t.organizationId = uuidField(body, "organizationId");
      t.name = str(body, "name");
      t.type = intEnum(body, "type") as LiftType;
      t.position = num(body, "position");
      t.carNum = num(body, "car_num", 0);
      t.vendorLiftPath = vendorPath(body, "vendor_lift_path", "vendorLiftPath");
      t.vendorGroupName = vendorPath(body, "vendor_group_name", "vendorGroupName");
      t.vendorPathsProvided = vendorPathsProvided(body);
      return t;
    },
  },
  {
    name: "UpdateLift",
    typeName: "Archive.Lift",
    buildT: (body) => {
      const t = new LiftT();
      t.id = uuidField(body, "id");
      t.bankId = uuidField(body, "bankId");
      t.organizationId = uuidField(body, "organizationId");
      t.name = str(body, "name");
      t.type = intEnum(body, "type") as LiftType;
      t.position = num(body, "position");
      t.carNum = num(body, "car_num", 0);
      t.vendorLiftPath = vendorPath(body, "vendor_lift_path", "vendorLiftPath");
      t.vendorGroupName = vendorPath(body, "vendor_group_name", "vendorGroupName");
      t.vendorPathsProvided = vendorPathsProvided(body);
      const rawFloors = (dual(body, "floors", "liftFloors") ?? []) as any[];
      // organizationId lives on the parent body, not on each floor entry
      const orgId = uuidField(body, "organizationId") ?? uuidField(body, "organization_id");
      t.liftFloors = rawFloors.map((f: any) => {
        const lf = new LiftFloorT();
        lf.id = uuidFieldOrZeroWhenAbsent(f, "id");
        lf.liftId = uuidField(f, "lift_id") ?? uuidField(f, "liftId");
        lf.bankFloorId = uuidField(f, "bank_floor_id") ?? uuidField(f, "bankFloorId");
        lf.organizationId =
          uuidField(f, "organization_id") ?? uuidField(f, "organizationId") ?? orgId;
        lf.frontDoor = bool(f, "front_door", true);
        lf.rearDoor = bool(f, "rear_door");
        return lf;
      });
      return t;
    },
  },

  // ---- User ----------------------------------------------------------------
  {
    name: "CreateUser",
    typeName: "Archive.User",
    buildT: (body) => {
      const t = new UserT();
      t.id = requiredUuidField(body, "id");
      t.orgId = stringToUuidT(str(body, "org_id"));
      t.roleId = stringToUuidT(str(body, "role_id"));
      t.email = str(body, "email");
      t.firstName = str(body, "first_name");
      t.middleName = str(body, "middle_name");
      t.lastName = str(body, "last_name");
      t.status = toUserStatus(str(body, "status"));
      t.prefTheme = str(body, "pref_theme");
      t.commandNotifications = bool(body, "command_notifications", true);
      t.globalNotifications = bool(body, "global_notifications", true);
      // INHERIT matches UserT's own schema default, and is what an unset user
      // must read as: taking the organization's value rather than an explicit
      // choice nobody made.
      t.actionFiringMode = toActionFiringMode(
        body.action_firing_mode as string | number | undefined,
        ActionFiringMode.INHERIT,
      );
      t.contactInfo = buildContactInfoFromBody(body);
      return t;
    },
  },
  {
    name: "UpdateUser",
    typeName: "Archive.User",
    buildT: (body) => {
      const t = new UserT();
      // uuidField handles UuidT instances, structuredClone POJOs (with .value),
      // and string UUIDs uniformly. The previous str()/stringToUuidT chain
      // returned "" for POJO-shaped ids (Redux serializability shim can strip
      // class prototypes), causing UserT.pack to throw "Required field id".
      t.id = uuidField(body, "id");
      t.orgId = uuidField(body, "org_id");
      t.roleId = uuidField(body, "role_id");
      t.prefGridId = uuidField(body, "pref_grid_id");
      t.email = str(body, "email");
      t.firstName = str(body, "first_name");
      t.middleName = str(body, "middle_name");
      t.lastName = str(body, "last_name");
      t.status = toUserStatus(str(body, "status"));
      t.prefTheme = str(body, "pref_theme");
      t.commandNotifications = bool(body, "command_notifications", true);
      t.globalNotifications = bool(body, "global_notifications", true);
      // INHERIT matches UserT's own schema default, and is what an unset user
      // must read as: taking the organization's value rather than an explicit
      // choice nobody made.
      t.actionFiringMode = toActionFiringMode(
        body.action_firing_mode as string | number | undefined,
        ActionFiringMode.INHERIT,
      );
      t.contactInfo = buildContactInfoFromBody(body);
      return t;
    },
  },

  // ---- Role ----------------------------------------------------------------
  {
    name: "CreateRole",
    typeName: "Archive.Role",
    buildT: (body) => {
      const t = new RoleT();
      t.id = requiredUuidField(body, "id");
      t.orgId = stringToUuidT(str(body, "org_id"));
      t.baseRoleId = stringToUuidT(str(body, "base_role_id"));
      t.name = str(body, "name");
      t.displayName = str(body, "display_name");
      t.active = bool(body, "active", true);
      const menu = body.menu;
      t.menuJson = menu != null ? JSON.stringify(menu) : null;
      t.permissionOverridesJson = nullable(body, "permission_overrides_json");
      // Pack entity-scoped permission grant vectors (bank / lift / floor).
      // Empty arrays are valid and mean "no restriction on that class" (allow-all).
      t.bankPermissions = buildBankPermissions(body);
      t.liftPermissions = buildLiftPermissions(body);
      t.floorPermissions = buildFloorPermissions(body);
      t.permissionGrants = buildRolePermissionGrants(body);
      return t;
    },
  },
  {
    name: "UpdateRole",
    typeName: "Archive.Role",
    buildT: (body) => {
      const t = new RoleT();
      // Same robustness fix as UpdateUser — uuidField handles UuidT/POJO/string
      // shapes so structuredClone'd ids from Redux don't break the pack.
      t.id = uuidField(body, "id");
      t.orgId = uuidField(body, "org_id");
      t.baseRoleId = uuidField(body, "base_role_id");
      t.name = str(body, "name");
      t.displayName = str(body, "display_name");
      t.active = bool(body, "active", true);
      const menu = body.menu;
      t.menuJson = menu != null ? JSON.stringify(menu) : null;
      t.permissionOverridesJson = nullable(body, "permission_overrides_json");
      // Pack entity-scoped permission grant vectors (bank / lift / floor).
      // Empty arrays are valid and mean "no restriction on that class" (allow-all).
      t.bankPermissions = buildBankPermissions(body);
      t.liftPermissions = buildLiftPermissions(body);
      t.floorPermissions = buildFloorPermissions(body);
      t.permissionGrants = buildRolePermissionGrants(body);
      return t;
    },
  },

  // ---- Key -----------------------------------------------------------------
  {
    name: "CreateKey",
    typeName: "Archive.Key",
    buildT: (body) => buildKeyT(body, requiredUuidField(body, "id")),
  },
  {
    name: "UpdateKey",
    typeName: "Archive.Key",
    buildT: (body) => buildKeyT(body, stringToUuidT(str(body, "id"))),
  },

  // ---- CustomKey -----------------------------------------------------------
  {
    name: "CreateCustomKey",
    typeName: "Archive.CustomKey",
    buildT: (body) => {
      const t = new CustomKeyT();
      t.id = requiredUuidField(body, "id");
      t.organizationId = stringToUuidT(str(body, "organization_id"));
      t.bankId = stringToUuidT(str(body, "bank_id"));
      t.fbsDeviceType = toLiftType(str(body, "fbs_device_type"));
      t.encodedType = num(body, "encoded_type");
      t.messageContent = num(body, "message_content");
      t.messageValue = num(body, "message_value");
      t.position = num(body, "position");
      return t;
    },
  },
  {
    name: "UpdateCustomKey",
    typeName: "Archive.CustomKey",
    buildT: (body) => {
      const t = new CustomKeyT();
      t.id = stringToUuidT(str(body, "id"));
      t.organizationId = stringToUuidT(str(body, "organization_id"));
      t.bankId = stringToUuidT(str(body, "bank_id"));
      t.fbsDeviceType = toLiftType(str(body, "fbs_device_type"));
      t.encodedType = num(body, "encoded_type");
      t.messageContent = num(body, "message_content");
      t.messageValue = num(body, "message_value");
      t.position = num(body, "position");
      return t;
    },
  },

  // ---- Notification --------------------------------------------------------
  {
    name: "CreateNotification",
    typeName: "Archive.Notification",
    buildT: (body) => {
      const t = new NotificationT();
      t.id = requiredUuidField(body, "id");
      t.bankId = stringToUuidT(str(body, "bank_id"));
      t.organizationId = stringToUuidT(str(body, "organization_id"));
      t.name = str(body, "name");
      t.description = str(body, "description");
      t.type = toNotificationType(str(body, "type"));
      t.fbsDeviceType = toLiftType(str(body, "fbs_device_type"));
      t.encodedType = num(body, "encoded_type");
      t.messageContent = num(body, "message_content");
      t.messageValue = num(body, "message_value");
      t.position = num(body, "position");
      t.cooldown = num(body, "cooldown", 300);
      t.minSpan = num(body, "min_span", 15);
      t.muted = bool(body, "muted");
      // bank_ids is the rule's real scope; bankId above stays for readers that
      // predate it. Sending both keeps an older cloud working unchanged.
      t.bankIds = uuidListField(body, "bank_ids");
      t.bankScope = toBankScope(str(body, "bank_scope"));
      t.matchMode = toMatchMode(str(body, "match_mode"));
      t.triggerEdge = toTriggerEdge(str(body, "trigger_edge"));
      t.channels = num(body, "channels");
      t.recipients = buildNotificationRecipientsFromBody(body);
      return t;
    },
  },
  {
    name: "UpdateNotification",
    typeName: "Archive.Notification",
    buildT: (body) => {
      const t = new NotificationT();
      t.id = stringToUuidT(str(body, "id"));
      t.bankId = stringToUuidT(str(body, "bank_id"));
      t.organizationId = stringToUuidT(str(body, "organization_id"));
      t.name = str(body, "name");
      t.description = str(body, "description");
      t.type = toNotificationType(str(body, "type"));
      t.fbsDeviceType = toLiftType(str(body, "fbs_device_type"));
      t.encodedType = num(body, "encoded_type");
      t.messageContent = num(body, "message_content");
      t.messageValue = num(body, "message_value");
      t.position = num(body, "position");
      t.cooldown = num(body, "cooldown", 300);
      t.minSpan = num(body, "min_span", 15);
      t.muted = bool(body, "muted");
      // bank_ids is the rule's real scope; bankId above stays for readers that
      // predate it. Sending both keeps an older cloud working unchanged.
      t.bankIds = uuidListField(body, "bank_ids");
      t.bankScope = toBankScope(str(body, "bank_scope"));
      t.matchMode = toMatchMode(str(body, "match_mode"));
      t.triggerEdge = toTriggerEdge(str(body, "trigger_edge"));
      t.channels = num(body, "channels");
      t.recipients = buildNotificationRecipientsFromBody(body);
      return t;
    },
  },

  // ---- ActionBank ----------------------------------------------------------
  // ActionBank uses a composite primary key (message_id, bank_id). Both
  // appear in the URL path on PUT/DELETE, but the packed body carries them
  // too, so create and update pack the same table with different id handling.
  {
    name: "CreateActionBank",
    typeName: "Archive.ActionBank",
    buildT: (body) => {
      const t = new ActionBankT();
      t.messageId = uuidField(body, "message_id");
      t.bankId = uuidField(body, "bank_id");
      t.organizationId = uuidField(body, "organization_id");
      t.actionId = uuidField(body, "action_id");
      t.toggleActionId = uuidField(body, "toggle_action_id");
      t.toggleMessageId = uuidField(body, "toggle_message_id");
      t.buttonType = toActionButtonType(str(body, "button_type"));
      t.label = str(body, "label");
      t.toggleLabel = str(body, "toggle_label");
      t.validStates = strArray(body, "valid_states");
      t.toggleValidStates = strArray(body, "toggle_valid_states");
      t.color = str(body, "color");
      t.color2 = str(body, "color2");
      t.toggleColor = str(body, "toggle_color");
      t.timer = num(body, "timer", 0);
      t.timer2 = num(body, "timer2", 0);
      t.toggleTimer = num(body, "toggle_timer", 0);
      return t;
    },
  },
  {
    name: "UpdateActionBank",
    typeName: "Archive.ActionBank",
    buildT: (body) => {
      const t = new ActionBankT();
      t.messageId = uuidField(body, "message_id");
      t.bankId = uuidField(body, "bank_id");
      t.organizationId = uuidField(body, "organization_id");
      t.actionId = uuidField(body, "action_id");
      t.toggleActionId = uuidField(body, "toggle_action_id");
      t.toggleMessageId = uuidField(body, "toggle_message_id");
      t.buttonType = toActionButtonType(str(body, "button_type"));
      t.label = str(body, "label");
      t.toggleLabel = str(body, "toggle_label");
      t.validStates = strArray(body, "valid_states");
      t.toggleValidStates = strArray(body, "toggle_valid_states");
      t.color = str(body, "color");
      t.color2 = str(body, "color2");
      t.toggleColor = str(body, "toggle_color");
      t.timer = num(body, "timer", 0);
      t.timer2 = num(body, "timer2", 0);
      t.toggleTimer = num(body, "toggle_timer", 0);
      return t;
    },
  },

  // ---- DebounceOverride ----------------------------------------------------
  {
    name: "CreateDebounceOverride",
    typeName: "Archive.DebounceOverride",
    buildT: (body) => {
      const t = new DebounceOverrideT();
      t.id = requiredUuidField(body, "id");
      t.bankId = stringToUuidT(str(body, "bank_id"));
      t.liftId = stringToUuidT(str(body, "lift_id"));
      t.organizationId = stringToUuidT(str(body, "organization_id"));
      t.fbsDeviceType = liftTypeField(body, "fbs_device_type");
      t.encodedType = num(body, "encoded_type");
      t.messageContent = num(body, "message_content");
      t.messageValue = num(body, "message_value");
      t.position = num(body, "position");
      t.debounceTime = num(body, "debounce_time");
      return t;
    },
  },
  {
    name: "UpdateDebounceOverride",
    typeName: "Archive.DebounceOverride",
    buildT: (body) => {
      const t = new DebounceOverrideT();
      t.id = stringToUuidT(str(body, "id"));
      t.bankId = stringToUuidT(str(body, "bank_id"));
      t.liftId = stringToUuidT(str(body, "lift_id"));
      t.organizationId = stringToUuidT(str(body, "organization_id"));
      t.fbsDeviceType = liftTypeField(body, "fbs_device_type");
      t.encodedType = num(body, "encoded_type");
      t.messageContent = num(body, "message_content");
      t.messageValue = num(body, "message_value");
      t.position = num(body, "position");
      t.debounceTime = num(body, "debounce_time");
      return t;
    },
  },

  // ---- TimedAction ---------------------------------------------------------
  // Create mints the id; update reads the one already on the body.
  {
    name: "CreateTimedAction",
    typeName: "Archive.TimedAction",
    buildT: (body) => buildTimedActionT(body, true),
  },
  {
    name: "UpdateTimedAction",
    typeName: "Archive.TimedAction",
    buildT: (body) => buildTimedActionT(body, false),
  },

  // ---- Configuration -------------------------------------------------------
  {
    name: "CreateConfiguration",
    typeName: "Api.Configuration",
    buildT: (body) => {
      const t = new ConfigurationT();
      t.id = requiredUuidField(body, "id");
      t.vendorId = stringToUuidT(str(body, "vendor_id"));
      t.name = str(body, "name");
      t.liftType = toLiftType(str(body, "lift_type"));
      t.lifts = num(body, "lifts");
      t.stops = num(body, "stops");
      t.rearDoor = bool(body, "rear_door");
      return t;
    },
  },
  {
    name: "UpdateConfiguration",
    typeName: "Api.Configuration",
    buildT: (body) => {
      const t = new ConfigurationT();
      t.id = stringToUuidT(str(body, "id"));
      t.vendorId = stringToUuidT(str(body, "vendor_id"));
      t.name = str(body, "name");
      t.liftType = toLiftType(str(body, "lift_type"));
      t.lifts = num(body, "lifts");
      t.stops = num(body, "stops");
      t.rearDoor = bool(body, "rear_door");
      return t;
    },
  },

  // ---- Group ---------------------------------------------------------------
  {
    name: "CreateGroup",
    typeName: "Api.Group",
    buildT: (body) => {
      const t = new GroupT();
      t.id = requiredUuidField(body, "id");
      t.organizationId = stringToUuidT(str(body, "organization_id"));
      t.name = str(body, "name");
      t.messageTypeId = num(body, "message_type_id");
      return t;
    },
  },
  {
    name: "UpdateGroup",
    typeName: "Api.Group",
    buildT: (body) => {
      const t = new GroupT();
      t.id = stringToUuidT(str(body, "id"));
      t.organizationId = stringToUuidT(str(body, "organization_id"));
      t.name = str(body, "name");
      t.messageTypeId = num(body, "message_type_id");
      return t;
    },
  },

  // ---- Protocol ------------------------------------------------------------
  {
    name: "CreateProtocol",
    typeName: "Api.Protocol",
    buildT: (body) => {
      const t = new ProtocolT();
      t.id = requiredUuidField(body, "id");
      t.name = str(body, "name");
      t.defaultInterfaceJson = nullable(body, "default_interface_json");
      t.cdoInterfaceJson = nullable(body, "cdo_interface_json");
      return t;
    },
  },
  {
    name: "UpdateProtocol",
    typeName: "Api.Protocol",
    buildT: (body) => {
      const t = new ProtocolT();
      t.id = stringToUuidT(str(body, "id"));
      t.name = str(body, "name");
      t.defaultInterfaceJson = nullable(body, "default_interface_json");
      t.cdoInterfaceJson = nullable(body, "cdo_interface_json");
      return t;
    },
  },

  // ---- Vendor --------------------------------------------------------------
  {
    name: "CreateVendor",
    typeName: "Api.Vendor",
    buildT: (body) => {
      const t = new VendorT();
      t.id = requiredUuidField(body, "id");
      t.name = str(body, "name");
      return t;
    },
  },
  {
    name: "UpdateVendor",
    typeName: "Api.Vendor",
    buildT: (body) => {
      const t = new VendorT();
      t.id = stringToUuidT(str(body, "id"));
      t.name = str(body, "name");
      return t;
    },
  },

  // ---- ChannelModifier -----------------------------------------------------
  {
    name: "CreateChannelModifier",
    typeName: "Archive.ChannelModifier",
    buildT: (body) => {
      const t = new ChannelModifierT();
      t.id = requiredUuidField(body, "id");
      t.sourceChannelId = uuidField(body, "sourceChannelId");
      t.targetChannelId = uuidField(body, "targetChannelId");
      t.name = str(body, "name");
      t.description = nullable(body, "description");
      t.modifierType = intEnum(body, "modifierType") as ModifierType;
      // The message kinds travel as the scalars. The legacy `*_message_type`
      // unions stay NONE: there is no message table to put in them, a
      // discriminant without one fails the generated verifier, and the Go
      // Object API drops it on unpack, so LiftCloud reads the scalars.
      t.sourceMessageTypeType = MessageContent.NONE;
      t.sourceMessageContent = num(body, "sourceMessageContent", 0);
      t.sourceMessageValue = num(body, "sourceMessageValue", 0);
      t.targetMessageTypeType = MessageContent.NONE;
      t.targetMessageContent = intEnum(body, "targetMessageType");
      t.direction = intEnum(body, "direction") as ModifierDirection;
      t.priority = num(body, "priority", 0);
      t.active = bool(body, "active", true);
      t.schemaVersion = num(body, "schemaVersion", 1);
      t.conditions = ((body.conditions as Record<string, unknown>[] | null | undefined) ?? []).map(
        (c) => {
          const cond = new ModifierConditionT();
          cond.fieldPath = str(c, "fieldPath");
          cond.op = intEnum(c, "op") as ConditionOp;
          cond.valueText = nullable(c, "valueText");
          return cond;
        },
      );
      t.actions = ((body.actions as Record<string, unknown>[] | null | undefined) ?? []).map(
        (a) => {
          const act = new ModifierActionT();
          act.fieldPath = str(a, "fieldPath");
          act.newValueText = nullable(a, "newValueText");
          return act;
        },
      );
      t.payload = messageTField(body);
      // Schema v3 value-transform directive. Without these reads the
      // operator's Passthrough / Specific / Transformative choice (and
      // any range/offset they set) would never reach the FBT — the
      // packed binary would have default PASSTHROUGH on every save.
      t.valueTransformMode = intEnum(body, "valueTransformMode") as ValueTransformMode;
      t.valueRangeStart = num(body, "valueRangeStart", 0);
      t.valueRangeEnd = num(body, "valueRangeEnd", 0);
      t.valueTransformOffset = num(body, "valueTransformOffset", 0);
      t.expectedSequence = expectedSequenceField(body);
      // Schema v6: conflict-resolution policy. Without this read the
      // operator's CANCEL_LOWER / OVERRIDE_LOWER / MERGE / QUEUE choice
      // would be silently dropped on every save — the packed binary would
      // always carry the COEXIST default (0). The matching field on the
      // form body is `conflictPolicy` (camelCase), set by handleSave.
      t.conflictPolicy = intEnum(body, "conflictPolicy") as ConflictPolicy;
      // Schema v7: payload state, fail-closed gate policy, post-drop action and
      // observe channel. Assigned here, in the site, rather than in a shared
      // helper, so the field-coverage check can see every one of them.
      t.payloadActive = intEnum(body, "payloadActive", ActiveState.UNSET) as ActiveState;
      t.timeoutPolicy = intEnum(
        body,
        "timeoutPolicy",
        ModifierTimeoutPolicy.RELEASE,
      ) as ModifierTimeoutPolicy;
      t.dropAction = intEnum(body, "dropAction", ModifierDropAction.NONE) as ModifierDropAction;
      t.observeChannelId = optionalUuidField(body, "observeChannelId");
      // Schema v8: conditions on current state, and the time budget for the
      // steps after RELEASE. Absent keys read as no conditions and 0 (the
      // runtime default), the pre-v8 meaning.
      t.preconditions = preconditionsField(body);
      t.postReleaseBudgetMs = num(body, "postReleaseBudgetMs", 0);
      return t;
    },
  },
  {
    name: "UpdateChannelModifier",
    typeName: "Archive.ChannelModifier",
    buildT: (body) => {
      const t = new ChannelModifierT();
      t.id = uuidField(body, "id");
      t.sourceChannelId = uuidField(body, "sourceChannelId");
      t.targetChannelId = uuidField(body, "targetChannelId");
      t.name = str(body, "name");
      t.description = nullable(body, "description");
      t.modifierType = intEnum(body, "modifierType") as ModifierType;
      // Kinds as scalars, unions NONE — see CreateChannelModifier above.
      t.sourceMessageTypeType = MessageContent.NONE;
      t.sourceMessageContent = num(body, "sourceMessageContent", 0);
      t.sourceMessageValue = num(body, "sourceMessageValue", 0);
      t.targetMessageTypeType = MessageContent.NONE;
      t.targetMessageContent = intEnum(body, "targetMessageType");
      t.direction = intEnum(body, "direction") as ModifierDirection;
      t.priority = num(body, "priority", 0);
      t.active = bool(body, "active", true);
      t.schemaVersion = num(body, "schemaVersion", 1);
      t.conditions = ((body.conditions as Record<string, unknown>[] | null | undefined) ?? []).map(
        (c) => {
          const cond = new ModifierConditionT();
          cond.fieldPath = str(c, "fieldPath");
          cond.op = intEnum(c, "op") as ConditionOp;
          cond.valueText = nullable(c, "valueText");
          return cond;
        },
      );
      t.actions = ((body.actions as Record<string, unknown>[] | null | undefined) ?? []).map(
        (a) => {
          const act = new ModifierActionT();
          act.fieldPath = str(a, "fieldPath");
          act.newValueText = nullable(a, "newValueText");
          return act;
        },
      );
      t.payload = messageTField(body);
      // Mirror the v3 fields onto updates so the operator can flip a
      // modifier between Passthrough / Specific / Transformative and
      // have the change actually round-trip through the FBT pack.
      t.valueTransformMode = intEnum(body, "valueTransformMode") as ValueTransformMode;
      t.valueRangeStart = num(body, "valueRangeStart", 0);
      t.valueRangeEnd = num(body, "valueRangeEnd", 0);
      t.valueTransformOffset = num(body, "valueTransformOffset", 0);
      t.expectedSequence = expectedSequenceField(body);
      // Schema v6: mirror conflict-resolution policy onto updates so an
      // operator can change a modifier's policy and have it reach the
      // wire. Mirrors the POST path above — both sides must write it or
      // PUT silently resets the policy to COEXIST on every save.
      t.conflictPolicy = intEnum(body, "conflictPolicy") as ConflictPolicy;
      // Schema v7, mirrored for the same reason: the FlatBuffer PUT carries the
      // full state, so an unassigned field would clear DROP, REVERT, INACTIVE
      // or the observe channel on every edit.
      t.payloadActive = intEnum(body, "payloadActive", ActiveState.UNSET) as ActiveState;
      t.timeoutPolicy = intEnum(
        body,
        "timeoutPolicy",
        ModifierTimeoutPolicy.RELEASE,
      ) as ModifierTimeoutPolicy;
      t.dropAction = intEnum(body, "dropAction", ModifierDropAction.NONE) as ModifierDropAction;
      t.observeChannelId = optionalUuidField(body, "observeChannelId");
      // Schema v8, mirrored for the same reason: an unassigned field would
      // clear the preconditions or the post-release budget on every edit.
      t.preconditions = preconditionsField(body);
      t.postReleaseBudgetMs = num(body, "postReleaseBudgetMs", 0);
      return t;
    },
  },

  // ---- Map -----------------------------------------------------------------
  {
    name: "CreateMap",
    typeName: "Api.Map",
    buildT: (body) => {
      const t = new MapT();
      t.id = requiredUuidField(body, "id");
      t.orgId = stringToUuidT(str(body, "org_id")) ?? stringToUuidT(str(body, "orgId"));
      t.name = str(body, "name");
      t.offlineUrl = nullable(body, "offline_url") ?? nullable(body, "offlineUrl");
      t.waypoints = Array.isArray(body.waypoints)
        ? body.waypoints.map((waypoint) => toWaypointT(waypoint))
        : [];

      if (Array.isArray(body.group_waypoints)) {
        t.groupWaypoints = body.group_waypoints.map((groupWaypoint) =>
          toGroupWaypointT(groupWaypoint),
        );
      } else if (Array.isArray(body.groupWaypoints)) {
        t.groupWaypoints = body.groupWaypoints.map((groupWaypoint) =>
          toGroupWaypointT(groupWaypoint),
        );
      } else {
        t.groupWaypoints = [];
      }

      if (Array.isArray(body.bank_waypoints)) {
        t.bankWaypoints = body.bank_waypoints.map((bankWaypoint) => toBankWaypointT(bankWaypoint));
      } else if (Array.isArray(body.bankWaypoints)) {
        t.bankWaypoints = body.bankWaypoints.map((bankWaypoint) => toBankWaypointT(bankWaypoint));
      } else {
        t.bankWaypoints = [];
      }

      t.createdAt = nullable(body, "created_at") ?? nullable(body, "createdAt");
      t.updatedAt = nullable(body, "updated_at") ?? nullable(body, "updatedAt");
      t.deletedAt = nullable(body, "deleted_at") ?? nullable(body, "deletedAt");
      t.mapImgUrl = nullable(body, "map_img_url") ?? nullable(body, "mapImgUrl");
      t.hasDisplayView = bool(body, "has_display_view", bool(body, "hasDisplayView", false));
      t.isMain = bool(body, "is_main", bool(body, "isMain", bool(body, "main", false)));
      return t;
    },
  },
  {
    name: "UpdateMap",
    typeName: "Api.Map",
    buildT: (body) => {
      const t = new MapT();
      t.id = uuidField(body, "id");
      t.orgId = uuidField(body, "org_id") ?? uuidField(body, "orgId");
      t.name = str(body, "name");
      t.offlineUrl = nullable(body, "offline_url") ?? nullable(body, "offlineUrl");
      t.waypoints = Array.isArray(body.waypoints)
        ? body.waypoints.map((waypoint) => toWaypointT(waypoint))
        : [];

      if (Array.isArray(body.group_waypoints)) {
        t.groupWaypoints = body.group_waypoints.map((groupWaypoint) =>
          toGroupWaypointT(groupWaypoint),
        );
      } else if (Array.isArray(body.groupWaypoints)) {
        t.groupWaypoints = body.groupWaypoints.map((groupWaypoint) =>
          toGroupWaypointT(groupWaypoint),
        );
      } else {
        t.groupWaypoints = [];
      }

      if (Array.isArray(body.bank_waypoints)) {
        t.bankWaypoints = body.bank_waypoints.map((bankWaypoint) => toBankWaypointT(bankWaypoint));
      } else if (Array.isArray(body.bankWaypoints)) {
        t.bankWaypoints = body.bankWaypoints.map((bankWaypoint) => toBankWaypointT(bankWaypoint));
      } else {
        t.bankWaypoints = [];
      }

      t.createdAt = nullable(body, "created_at") ?? nullable(body, "createdAt");
      t.updatedAt = nullable(body, "updated_at") ?? nullable(body, "updatedAt");
      t.deletedAt = nullable(body, "deleted_at") ?? nullable(body, "deletedAt");
      t.mapImgUrl = nullable(body, "map_img_url") ?? nullable(body, "mapImgUrl");
      t.hasDisplayView = bool(body, "has_display_view", bool(body, "hasDisplayView", false));
      t.isMain = bool(body, "is_main", bool(body, "isMain", bool(body, "main", false)));
      return t;
    },
  },
  {
    name: "CreateWaypoint",
    typeName: "Api.Waypoint",
    buildT: (body) => buildWaypointMutationT(body),
  },
  {
    name: "UpdateWaypoint",
    typeName: "Api.Waypoint",
    buildT: (body) => buildWaypointMutationT(body),
  },
  {
    name: "CreateGroupWaypoint",
    typeName: "Api.GroupWaypoint",
    buildT: (body) => buildGroupWaypointMutationT(body),
  },
  {
    name: "UpdateGroupWaypoint",
    typeName: "Api.GroupWaypoint",
    buildT: (body) => buildGroupWaypointMutationT(body),
  },
  {
    name: "CreateBankWaypoint",
    typeName: "Api.BankWaypoint",
    buildT: (body) => buildBankWaypointMutationT(body),
  },
  {
    name: "UpdateBankWaypoint",
    typeName: "Api.BankWaypoint",
    buildT: (body) => buildBankWaypointMutationT(body),
  },

  // ---- SimulationScenario --------------------------------------------------
  // The scenario builder keeps the generated `SimulationScenarioT` as its state,
  // so create and update pack that value as it is; the server assigns the id on
  // create and keeps the one in the URL on update.
  {
    name: "SaveSimulationScenario",
    typeName: "Api.SimulationScenario",
    buildT: (body) => {
      const { scenario } = body;
      if (!(scenario instanceof SimulationScenarioT)) {
        throw new TypeError("SaveSimulationScenario: body.scenario must be a SimulationScenarioT");
      }
      return scenario;
    },
  },
];

/**
 * Initializes all request serializers by computing schema hashes and
 * registering them. Called once at application startup alongside
 * `initializeSchemaRegistry()`.
 */
export async function initializeRequestSerializers(): Promise<void> {
  const promises = serializerQueue.map(async (spec) => {
    const schemaHash = await getOrComputeHash(spec.typeName);
    registerRequestSerializer(spec.name, {
      name: spec.name,
      schemaHash,
      pack: (body: unknown) => {
        const obj = body as Record<string, unknown>;
        const t = spec.buildT(obj);
        const builder = new flatbuffers.Builder(1024);
        const offset = t.pack(builder);
        builder.finish(offset);
        return builder.asUint8Array();
      },
    });
  });
  await Promise.all(promises);
}
